// src/pages/portal/StakeholderDashboard.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import SAAM_HEADER_IMAGE from "../../assets/SAAM_HEADING.png";
import REPORT_COVER_IMAGE from "../../assets/report-cover-godown.jpg";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* =========================================================
   PREMIUM STAKEHOLDER DASHBOARD
   - Existing workflow system preserved
   - Report tab added
   - Premium PDF report with SAAM header
   - Report summary + closing note + signature
   - Report PDF + report record table
   - Mail queue to tender@shineagri.org
========================================================= */

const cx = (...xs) => xs.filter(Boolean).join(" ");

const OFFICE_TYPE_LABELS = {
  BANK: "Bank / Financing Agency",
  DISTRICT_REG: "District Registrar Office",
  STATE_REG: "State Registrar Office",
  CENTRAL_AGENCY: "Central Agency",
  NODAL_AGENCY: "Nodal Agency",
};

const OFFICE_TYPE_SHORT = {
  BANK: "BANK",
  DISTRICT_REG: "DR",
  STATE_REG: "SR",
  CENTRAL_AGENCY: "CA",
  NODAL_AGENCY: "NA",
};

const PROJECT_STAGE_CODES = [
  "MOU",
  "SURVEY",
  "SOIL",
  "DESIGN",
  "TENDER",
  "WORK",
  "EXCAVATION",
  "FOOTING",
  "PLINTH",
  "COLUMN_BEAM",
  "BRICK",
  "PLASTER",
  "PEB_INSPECTION",
  "PEB_INSTALL",
  "COLOUR",
  "LOGO",
  "FINISH",
  "DONE",
];

const PROJECT_STAGE_LABELS = {
  MOU: "MOU",
  SURVEY: "Site Survey",
  SOIL: "Soil Testing",
  DESIGN: "Design & Structure",
  TENDER: "Tender Process",
  WORK: "Work Started",
  EXCAVATION: "Excavation / Site Work",
  FOOTING: "Footing",
  PLINTH: "Plinth Beam",
  COLUMN_BEAM: "Column & Beam Work",
  BRICK: "Brick Masonry Work",
  PLASTER: "Plaster Work",
  PEB_INSPECTION: "PEB Inspection",
  PEB_INSTALL: "PEB Installation",
  COLOUR: "Colour Work",
  LOGO: "Logo Design & Branding",
  FINISH: "Finishing",
  DONE: "Completed",
};

const CONSTRUCTION_TIMELINE_STAGES = [
  { key: "EXCAVATION", label: "Excavation / Site Work" },
  { key: "FOOTING", label: "Footing" },
  { key: "PLINTH_BEAM", label: "Plinth Beam" },
  { key: "COLUMNS_BEAMS", label: "Column & Beam Work" },
  { key: "BRICK_MASONRY", label: "Brick Masonry Work" },
  { key: "PLASTER", label: "Plaster Work" },
  { key: "PEB_INSPECTION", label: "PEB Inspection" },
  { key: "PEB_INSTALLATION", label: "PEB Installation" },
  { key: "COLOUR_WORK", label: "Colour Work" },
  { key: "LOGO_BRANDING", label: "Logo Design & Branding" },
  { key: "FINISHING", label: "Finishing" },
  { key: "COMPLETED", label: "Completed" },
];

const PER_PAGE = 8;

/* -------------------- format helpers -------------------- */

function formatDate(ts, withTime = true) {
  try {
    if (!ts) return "—";

    let d = null;

    if (typeof ts.toDate === "function") d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === "number") d = new Date(ts > 1e12 ? ts : ts * 1000);
    else if (typeof ts === "string") d = new Date(ts);
    else if (ts.seconds) d = new Date(ts.seconds * 1000);

    if (!d || Number.isNaN(d.getTime())) return "—";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    if (!withTime) return `${dd}/${mm}/${yyyy}`;

    let hh = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;

    return `${dd}/${mm}/${yyyy}, ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
  } catch {
    return "—";
  }
}

function tsValue(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts > 1e12 ? ts : ts * 1000;
    if (typeof ts === "string") return new Date(ts).getTime() || 0;
    return ts.seconds ? ts.seconds * 1000 : 0;
  } catch {
    return 0;
  }
}

function latestTimestamp(...items) {
  const flat = items.flat().filter(Boolean);
  if (!flat.length) return null;
  return flat.sort((a, b) => tsValue(b) - tsValue(a))[0] || null;
}

function formatPDFINR(n) {
  const clean = String(n || "0").replace(/[^0-9.-]/g, "");
  const num = Number(clean || 0);
  return `Rs. ${num.toLocaleString("en-IN")}`;
}

function normalStatus(s = "") {
  const v = String(s || "").toUpperCase();

  if (v === "COMPLETED" || v === "DONE") return "COMPLETED";
  if (v === "IN_PROGRESS" || v === "IN PROGRESS") return "IN_PROGRESS";
  if (v === "REJECTED") return "REJECTED";
  if (v === "ASSIGNED") return "ASSIGNED";
  if (v === "APPROVED_TO_WORKFLOW") return "APPROVED_TO_WORKFLOW";

  return v || "PENDING";
}

function calcProjectStageFromProgress(progress) {
  const p = Number(progress || 0);

  if (p <= 0) return "Not Started";
  if (p >= 100) return "Completed";
  if (p >= 75) return "Near Completion";

  return "In Progress";
}

function computeStagesProgress(stages = []) {
  const total = stages.length || 1;
  const done = stages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed"
  ).length;

  return Math.round((done / total) * 100);
}

function getProjectStageLine(projectDoc = {}) {
  const stages = Array.isArray(projectDoc.stages) ? projectDoc.stages : [];
  if (!stages.length) return "";

  const current = projectDoc.currentStage || "";
  const progress =
    typeof projectDoc.progressPercent === "number"
      ? projectDoc.progressPercent
      : computeStagesProgress(stages);

  return `${current || "—"} (${progress}%)`;
}

function normalizeConstructionTimeline(godown = {}, projectDoc = {}) {
  const base = godown?.constructionTimeline || {};
  const stages = Array.isArray(projectDoc?.stages) ? projectDoc.stages : [];
  const merged = { ...base };

  const map = {
    EXCAVATION: "EXCAVATION",
    FOOTING: "FOOTING",
    PLINTH: "PLINTH_BEAM",
    COLUMN_BEAM: "COLUMNS_BEAMS",
    BRICK: "BRICK_MASONRY",
    PLASTER: "PLASTER",
    PEB_INSPECTION: "PEB_INSPECTION",
    PEB_INSTALL: "PEB_INSTALLATION",
    COLOUR: "COLOUR_WORK",
    LOGO: "LOGO_BRANDING",
    FINISH: "FINISHING",
    DONE: "COMPLETED",
  };

  stages.forEach((s) => {
    const key = map[s.code];
    if (!key) return;

    if (!merged[key]) {
      merged[key] = {
        status:
          String(s.status || "").toLowerCase() === "completed"
            ? "COMPLETED"
            : String(s.status || "").toLowerCase() === "in progress"
            ? "IN_PROGRESS"
            : String(s.status || "").toLowerCase() === "rejected"
            ? "REJECTED"
            : "PENDING",
        note: s.lastStageNote || "",
        updatedAt: s.completedAt || s.startedAt || s.lastStageUpdatedAt || null,
      };
    }
  });

  return merged;
}

function computeConstructionProgressFromTimeline(timeline = {}) {
  const total = CONSTRUCTION_TIMELINE_STAGES.length || 1;
  const done = CONSTRUCTION_TIMELINE_STAGES.filter((s) => {
    const st = normalStatus(timeline?.[s.key]?.status);
    return st === "COMPLETED";
  }).length;

  return Math.round((done / total) * 100);
}

/* -------------------- godown application helpers -------------------- */

function getGodownApplicationDocGroups(godown = {}) {
  const docs = godown.documents || {};

  return [
    { title: "Registration Certificate", files: docs.registrationDocs || [] },
    { title: "Land Documents", files: docs.landDocs || [] },
    { title: "Committee / Board Resolution", files: docs.committeeDocs || [] },
    { title: "Audit Reports", files: docs.auditDocs || [] },
    { title: "Other Supporting Documents", files: docs.otherDocs || [] },
  ].filter((g) => Array.isArray(g.files) && g.files.length > 0);
}

function getAllGodownDocumentCount(godown = {}) {
  return getGodownApplicationDocGroups(godown).reduce(
    (sum, group) => sum + (group.files?.length || 0),
    0
  );
}

function calculateApplicationReadiness(godown = {}) {
  const docs = godown.documents || {};
  const fin = godown.financialDetails || {};

  let score = 0;

  if (godown.pacsName) score += 8;
  if (godown.pacsRegistrationNo) score += 8;
  if (godown.contactPhone) score += 6;
  if (godown.district && godown.taluka && (godown.village || godown.locationVillage)) {
    score += 10;
  }
  if (godown.capacityMt) score += 8;

  if ((docs.registrationDocs || []).length) score += 10;
  if ((docs.landDocs || []).length) score += 12;
  if ((docs.committeeDocs || []).length) score += 10;
  if ((docs.auditDocs || []).length) score += 12;

  if ((fin.profits || []).length >= 3) score += 8;
  if (fin.reserveFund || fin.buildingFund || fin.shareCapital) score += 8;

  return Math.min(100, score);
}

function getApplicationMissingItems(godown = {}) {
  const docs = godown.documents || {};
  const fin = godown.financialDetails || {};
  const missing = [];

  if (!godown.pacsName) missing.push("Institution Name");
  if (!godown.pacsRegistrationNo) missing.push("Registration No.");
  if (!godown.contactPhone) missing.push("Mobile No.");
  if (!godown.district) missing.push("District");
  if (!godown.taluka) missing.push("Taluka");
  if (!(godown.village || godown.locationVillage)) missing.push("Village");
  if (!godown.capacityMt) missing.push("Capacity");

  if (!(docs.registrationDocs || []).length) missing.push("Registration Certificate");
  if (!(docs.landDocs || []).length) missing.push("Land Documents");
  if (!(docs.committeeDocs || []).length) missing.push("Board Resolution");
  if (!(docs.auditDocs || []).length) missing.push("Last 3 Years Audit Reports");

  if (!(fin.profits || []).length) missing.push("Last 3 Years Profit Details");

  return missing;
}
/* -------------------- premium UI atoms -------------------- */

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
    yellow: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
    red: "bg-red-100 text-red-700 ring-1 ring-red-200",
    gray: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
    blue: "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
    purple: "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
    dark: "bg-slate-900 text-white ring-1 ring-slate-700",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function MetricCard({ label, value, note, tone = "emerald", icon = "◆" }) {
  const map = {
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-sky-500 to-blue-600",
    amber: "from-amber-400 to-orange-500",
    purple: "from-violet-500 to-purple-600",
    slate: "from-slate-700 to-slate-950",
    rose: "from-rose-500 to-red-600",
  };

  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
      <div
        className={cx(
          "absolute inset-x-0 top-0 h-1 bg-gradient-to-r",
          map[tone] || map.emerald
        )}
      />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
              {label}
            </div>

            <div className="mt-2 text-3xl font-black tracking-tight text-slate-950">
              {value}
            </div>
          </div>

          <div
            className={cx(
              "grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br text-white shadow-lg",
              map[tone] || map.emerald
            )}
          >
            <span className="text-lg">{icon}</span>
          </div>
        </div>

        {note && <div className="mt-3 text-[12px] leading-5 text-slate-500">{note}</div>}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, right }) {
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
      {(title || right) && (
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            {title && <h2 className="text-sm font-black text-slate-900">{title}</h2>}
            {subtitle && <p className="mt-1 text-[12px] leading-5 text-slate-500">{subtitle}</p>}
          </div>

          {right}
        </div>
      )}

      <div className="p-5">{children}</div>
    </div>
  );
}

function ProgressBar({ value, compact = false }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div
      className={cx(
        "w-full overflow-hidden rounded-full bg-slate-200",
        compact ? "h-2" : "h-3"
      )}
    >
      <div
        className={cx(
          "h-full rounded-full transition-all duration-500",
          pct < 25
            ? "bg-gradient-to-r from-orange-400 to-amber-500"
            : pct < 50
            ? "bg-gradient-to-r from-yellow-400 to-amber-500"
            : pct < 75
            ? "bg-gradient-to-r from-sky-400 to-blue-500"
            : "bg-gradient-to-r from-emerald-500 to-teal-600"
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function EmptyState({ title = "No records found", note = "Try changing filters." }) {
  return (
    <div className="grid place-items-center rounded-3xl border border-dashed border-slate-300 bg-slate-50/80 px-6 py-12 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-2xl shadow-sm">
        📄
      </div>

      <div className="mt-3 text-sm font-black text-slate-800">{title}</div>
      <div className="mt-1 text-[12px] text-slate-500">{note}</div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 flex items-center justify-center">
        <div className="rounded-3xl border border-emerald-100 bg-white px-8 py-6 text-center shadow-xl">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600" />
          <div className="mt-4 text-sm font-bold text-slate-700">
            Loading stakeholder dashboard…
          </div>
        </div>
      </div>
    </>
  );
}

function DocumentLink({ file, index }) {
  return (
    <a
      href={file.url || file.link || file.fileUrl || "#"}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[12px] shadow-sm hover:border-emerald-300 hover:bg-emerald-50"
    >
      <span className="min-w-0 truncate font-semibold text-slate-700">
        {file.name || file.title || `Document ${index + 1}`}
      </span>

      <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-1 text-[10px] font-bold text-white">
        Download
      </span>
    </a>
  );
}

function ApplicationDocViewer({ godown }) {
  const groups = getGodownApplicationDocGroups(godown);
  const missing = getApplicationMissingItems(godown);
  const readiness = calculateApplicationReadiness(godown);
  const fin = godown?.financialDetails || {};
  const profits = Array.isArray(fin.profits) ? fin.profits : [];

  if (!godown?.applicationId && groups.length === 0) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
        No direct application data found yet.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-700">
              Direct Godown Application
            </div>

            <div className="mt-2 font-mono text-sm font-black text-slate-900">
              {godown.applicationId || "—"}
            </div>

            <div className="mt-1 text-[12px] text-slate-600">
              Submitted: {formatDate(godown.createdAt)}
            </div>
          </div>

          <div className="w-full rounded-3xl border border-white bg-white/80 p-4 shadow-sm md:w-56">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                  AI Readiness
                </div>

                <div className="mt-1 text-3xl font-black text-emerald-700">
                  {readiness}%
                </div>
              </div>

              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-600 text-white shadow-lg">
                ✦
              </div>
            </div>

            <div className="mt-3">
              <ProgressBar value={readiness} />
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Reserve Fund
            </div>
            <div className="mt-1 text-sm font-black text-slate-900">
              {formatPDFINR(fin.reserveFund)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Building Fund
            </div>
            <div className="mt-1 text-sm font-black text-slate-900">
              {formatPDFINR(fin.buildingFund)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Share Capital
            </div>
            <div className="mt-1 text-sm font-black text-slate-900">
              {formatPDFINR(fin.shareCapital)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Avg. Profit
            </div>
            <div className="mt-1 text-sm font-black text-slate-900">
              {formatPDFINR(fin?.totals?.averageProfit)}
            </div>
          </div>
        </div>
      </div>

      {profits.length > 0 && (
        <Panel
          title="Last 3 Years Profit"
          subtitle="Financial summary submitted by applicant institution."
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[12px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Financial Year</th>
                  <th className="px-4 py-3 text-right">Net Profit</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {profits.map((p, i) => (
                  <tr key={i} className="bg-white">
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {p.fy || `Year ${i + 1}`}
                    </td>
                    <td className="px-4 py-3 text-right font-black text-slate-900">
                      {formatPDFINR(p.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
            <Panel
        title="Uploaded Application Documents"
        subtitle="All documents submitted by PACS / institution are available for review."
      >
        {groups.length === 0 ? (
          <EmptyState
            title="No documents uploaded"
            note="Applicant documents will appear here once uploaded."
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {groups.map((group) => (
              <div
                key={group.title}
                className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="font-black text-slate-800">{group.title}</div>

                  <Badge color="blue">{group.files.length} file(s)</Badge>
                </div>

                <div className="space-y-2">
                  {group.files.map((f, i) => (
                    <DocumentLink key={i} file={f} index={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {missing.length > 0 ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-5">
          <div className="text-sm font-black text-red-700">
            Missing / To be verified
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {missing.map((m) => (
              <Badge key={m} color="red">
                {m}
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-bold text-emerald-700">
          Application appears complete for preliminary scrutiny.
        </div>
      )}
    </div>
  );
}

function StageCard({ stage }) {
  const raw = String(stage.status || "").toLowerCase();

  let color = "gray";
  if (raw === "completed") color = "green";
  else if (raw === "in progress") color = "blue";
  else if (raw === "rejected") color = "red";
  else if (raw === "pending") color = "yellow";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-black text-slate-800">
            {stage.name || stage.code}
          </div>

          <div className="mt-1 text-[11px] text-slate-500">
            {formatDate(
              stage.completedAt ||
                stage.startedAt ||
                stage.rejectedAt ||
                stage.lastStageUpdatedAt
            )}
          </div>
        </div>

        <Badge color={color}>{stage.status || "Pending"}</Badge>
      </div>

      {stage.lastStageNote && (
        <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-[12px] leading-5 text-slate-600">
          {stage.lastStageNote}
        </div>
      )}
    </div>
  );
}

function ConstructionStageCard({ stage, data }) {
  const rawStatus = normalStatus(data?.status);
  const note = data?.note || data?.remark || "";
  const files = Array.isArray(data?.files) ? data.files : [];

  let labelStatus = "Pending";
  let badgeColor = "gray";

  if (rawStatus === "COMPLETED") {
    labelStatus = "Completed";
    badgeColor = "green";
  } else if (rawStatus === "IN_PROGRESS") {
    labelStatus = "In Progress";
    badgeColor = "blue";
  } else if (rawStatus === "REJECTED") {
    labelStatus = "Rejected";
    badgeColor = "red";
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-black text-slate-800">{stage.label}</div>

          <div className="mt-2 space-y-1 text-[11px] text-slate-500">
            <div>
              <b>Start:</b> {data?.startDate || data?.date || "—"}
            </div>

            <div>
              <b>Complete:</b> {data?.completeDate || data?.endDate || "—"}
            </div>

            <div>
              <b>Last Update:</b>{" "}
              {formatDate(data?.updatedAt || data?.date, false)}
            </div>
          </div>
        </div>

        <Badge color={badgeColor}>{labelStatus}</Badge>
      </div>

      {note && (
        <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-[12px] leading-5 text-slate-600">
          {note}
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="text-[11px] font-black text-emerald-700">
            Uploaded Files: {files.length}
          </div>

          {files.slice(0, 3).map((f, i) => (
            <DocumentLink key={i} file={f} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function safeText(v, fallback = "—") {
  const s = String(v ?? "").trim();
  return s || fallback;
}

function getReportFileUrls(stageData = {}) {
  const files = Array.isArray(stageData.files) ? stageData.files : [];

  return files
    .map((f) => ({
      name: f.name || f.title || f.fileName || "Uploaded File",
      url: f.url || f.link || f.fileUrl || "",
      type: f.type || f.mimeType || "",
    }))
    .filter((f) => f.url);
}

function makeReportFileName(project = {}, reportDate = "") {
  const godown = project._godown || {};
  const base = safeText(godown.applicationId || project.pacsName || project.id, "Project")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `${base}_Detailed_Report_${reportDate || dayjs().format("YYYY-MM-DD")}.pdf`;
}

async function generateDetailedProjectReportPdf(project = {}, reportDate = "") {
  const godown = project._godown || {};
  const mainProject = project._projectDashboard || {};
  const fin = godown.financialDetails || {};
  const tasks = Array.isArray(project._tasks) ? project._tasks : [];
  const timeline = godown.constructionTimeline || {};

  const reportDateText = reportDate
    ? dayjs(reportDate).format("DD MMM YYYY")
    : dayjs().format("DD MMM YYYY");

  const generatedAt = dayjs().format("DD MMM YYYY, hh:mm A");

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const NAVY = [5, 18, 38];
  const GREEN = [0, 83, 58];
  const DARK_GREEN = [0, 61, 43];
  const GOLD = [190, 137, 43];
  const LIGHT = [248, 250, 252];
  const BORDER = [214, 226, 232];
  const TEXT = [20, 31, 45];
  const MUTED = [90, 103, 122];

  const margin = 32;
  let y = 40;

  const clean = (v, f = "—") => {
    const s = String(v ?? "").trim();
    return s || f;
  };

  const stageFiles = (stageData = {}) => {
    const arr = Array.isArray(stageData.files) ? stageData.files : [];
    return arr
      .map((f) => ({
        name: f.name || f.fileName || "Photo",
        url: f.url || f.link || f.fileUrl || "",
        type: f.type || f.mimeType || "",
      }))
      .filter((f) => f.url);
  };

  const imageToBase64 = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch {
      return "";
    }
  };

  const pageHeader = (title = "") => {
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageW, pageH, "F");

    pdf.addImage(SAAM_HEADER_IMAGE, "PNG", 30, 14, pageW - 60, 60);

    pdf.setDrawColor(...GOLD);
    pdf.setLineWidth(1.2);
    pdf.line(55, 88, pageW - 55, 88);

    if (title) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.setTextColor(...GREEN);
      pdf.text(title, margin, 120);

      pdf.setDrawColor(...GOLD);
      pdf.line(margin, 130, pageW - margin, 130);
      y = 152;
    } else {
      y = 110;
    }
  };

  const footer = () => {
    const pages = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= pages; i += 1) {
      pdf.setPage(i);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(90);
      pdf.text(`Page ${i} of ${pages}`, pageW / 2, pageH - 14, {
        align: "center",
      });
    }
  };

  const sectionHeader = (title) => {
    if (y > pageH - 90) {
      pdf.addPage();
      pageHeader(title);
      return;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(...GREEN);
    pdf.text(title, margin, y);

    pdf.setDrawColor(...GOLD);
    pdf.setLineWidth(1);
    pdf.line(margin, y + 8, pageW - margin, y + 8);
    y += 26;
  };

  const greenTitle = (x, yy, w, title) => {
    pdf.setFillColor(...GREEN);
    pdf.rect(x, yy, w, 22, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(255, 255, 255);
    pdf.text(title, x + 8, yy + 15);
  };

  const infoTable = (x, yy, w, rows, title = "") => {
    if (title) greenTitle(x, yy, w, title);

    autoTable(pdf, {
      body: rows,
      startY: title ? yy + 22 : yy,
      theme: "grid",
      tableWidth: w,
      margin: { left: x, right: pageW - x - w },
      styles: {
        fontSize: 6.8,
        cellPadding: 4,
        lineColor: BORDER,
        lineWidth: 0.4,
        valign: "top",
      },
      columnStyles: {
        0: { cellWidth: w * 0.43, fontStyle: "bold", fillColor: LIGHT },
        1: { cellWidth: w * 0.57 },
      },
    });

    return pdf.lastAutoTable.finalY;
  };

  const coverInfo = (icon, label, value, yy) => {
    pdf.setFillColor(...GREEN);
    pdf.circle(54, yy + 6, 15, "F");
    pdf.setDrawColor(...GOLD);
    pdf.circle(54, yy + 6, 16.5, "S");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    pdf.text(label, 84, yy);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.8);
    pdf.setTextColor(...TEXT);
    pdf.text(value, 84, yy + 14, { maxWidth: 190 });

    pdf.setDrawColor(...GOLD);
    pdf.setLineWidth(0.4);
    pdf.line(84, yy + 26, 255, yy + 26);
  };

  const statusBadge = (x, yy, status) => {
    const s = clean(status, "PENDING").toUpperCase();
    let bg = [226, 232, 240];
    let fg = [30, 41, 59];

    if (s.includes("COMPLETE") || s.includes("APPROVED") || s.includes("AWARD")) {
      bg = [220, 252, 231];
      fg = [22, 101, 52];
    } else if (s.includes("PROGRESS")) {
      bg = [254, 243, 199];
      fg = [146, 64, 14];
    }

    pdf.setFillColor(...bg);
    pdf.roundedRect(x, yy, 82, 18, 8, 8, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.2);
    pdf.setTextColor(...fg);
    pdf.text(s, x + 41, yy + 12, { align: "center" });
  };

  /* ================= PREMIUM COVER PAGE ================= */
pdf.setFillColor(255, 255, 255);
pdf.rect(0, 0, pageW, pageH, "F");

/* SAAM Header */
pdf.addImage(SAAM_HEADER_IMAGE, "PNG", 34, 18, pageW - 68, 76);

pdf.setDrawColor(...GOLD);
pdf.setLineWidth(1.2);
pdf.line(150, 104, pageW - 60, 104);

/* Light grey diagonal background */
pdf.setFillColor(248, 250, 252);
pdf.triangle(0, 160, 360, 105, 0, pageH - 80, "F");

pdf.setFillColor(241, 245, 249);
pdf.triangle(120, 165, 420, 115, 120, pageH - 95, "F");

/* Right premium green shape */
pdf.setFillColor(...DARK_GREEN);
pdf.triangle(pageW - 260, 120, pageW, 80, pageW, pageH - 70, "F");
pdf.rect(pageW - 250, 210, 250, pageH - 280, "F");

/* Gold photo frame polygon */
pdf.setFillColor(...GOLD);
pdf.triangle(300, 185, pageW - 38, 130, 300, 690, "F");

pdf.setFillColor(255, 255, 255);
pdf.triangle(315, 195, pageW - 50, 150, 315, 665, "F");

/* Image frame */
pdf.setFillColor(...DARK_GREEN);
pdf.roundedRect(310, 190, 250, 350, 18, 18, "F");

try {
  pdf.addImage(REPORT_COVER_IMAGE, "JPEG", 318, 205, 234, 310);
} catch {}

/* Official report badge */
pdf.setFillColor(...DARK_GREEN);
pdf.roundedRect(382, 145, 170, 54, 12, 12, "F");

pdf.setDrawColor(...GOLD);
pdf.setLineWidth(1.3);
pdf.roundedRect(382, 145, 170, 54, 12, 12, "S");

pdf.setFont("helvetica", "bold");
pdf.setFontSize(8);
pdf.setTextColor(255, 255, 255);
pdf.text("OFFICIAL SYSTEM", 430, 166);

pdf.setTextColor(...GOLD);
pdf.text("GENERATED REPORT", 430, 180);

pdf.setFontSize(7);
pdf.setTextColor(255, 255, 255);
pdf.text("VERIFIED PROJECT REPORT", 430, 193);

/* Main Title */
pdf.setFont("helvetica", "bold");
pdf.setFontSize(26);
pdf.setTextColor(...NAVY);
pdf.text("PROJECT", 36, 235);

pdf.setFontSize(31);
pdf.setTextColor(...GREEN);
pdf.text("MONITORING", 36, 275);
pdf.text("DOSSIER", 36, 314);

pdf.setFont("helvetica", "normal");
pdf.setFontSize(10);
pdf.setTextColor(...NAVY);
pdf.text("DETAILED SCRUTINY, TENDER &", 36, 354);
pdf.text("CONSTRUCTION PROGRESS REPORT", 36, 370);

pdf.setDrawColor(...GOLD);
pdf.setLineWidth(2);
pdf.line(36, 398, 112, 398);

/* Cover info rows */
const coverInfoRow = (yy, label, value, iconText) => {
  pdf.setFillColor(...GREEN);
  pdf.circle(54, yy + 8, 17, "F");

  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(1.2);
  pdf.circle(54, yy + 8, 19, "S");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  pdf.text(iconText, 54, yy + 11, { align: "center" });

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.setTextColor(...MUTED);
  pdf.text(label, 84, yy);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.8);
  pdf.setTextColor(...TEXT);

  pdf.text(String(value || "—"), 84, yy + 15, {
    maxWidth: 195,
  });

  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(0.5);
  pdf.line(84, yy + 30, 265, yy + 30);
};

coverInfoRow(
  440,
  "Application ID",
  clean(godown.applicationId || project.id),
  "ID"
);

coverInfoRow(
  500,
  "PACS / Institution",
  clean(project.pacsName || godown.pacsName),
  "P"
);

coverInfoRow(
  575,
  "District",
  clean(project.district || godown.district),
  "D"
);

coverInfoRow(
  635,
  "Capacity",
  `${clean(project.capacityMt || godown.capacityMt)} MT`,
  "MT"
);

coverInfoRow(
  695,
  "Report Date",
  reportDateText,
  "DT"
);

/* Bottom footer band */
pdf.setFillColor(...DARK_GREEN);
pdf.rect(0, pageH - 72, pageW, 72, "F");

pdf.setDrawColor(...GOLD);
pdf.setLineWidth(2);
pdf.line(0, pageH - 72, pageW, pageH - 72);

pdf.setFillColor(...GREEN);
pdf.circle(56, pageH - 38, 18, "F");

pdf.setFont("helvetica", "bold");
pdf.setFontSize(8);
pdf.setTextColor(255, 255, 255);
pdf.text("SAAM PROJECT MONITORING SYSTEM", 86, pageH - 44);

pdf.setFont("helvetica", "normal");
pdf.setFontSize(7);
pdf.text(
  "Transparent Monitoring  |  Accurate Reporting  |  Better Governance",
  86,
  pageH - 29
);

pdf.setFont("helvetica", "bold");
pdf.setFontSize(8.5);
pdf.text("www.saam.coop", pageW - 52, pageH - 38, { align: "right" });

/* ================= EXECUTIVE SUMMARY ================= */
  pdf.addPage();
  pageHeader("EXECUTIVE SUMMARY");

  const kpiW = (pageW - 64 - 5 * 8) / 6;
  const kpiY = y;

  const kpis = [
    ["FILE", clean(godown.applicationId || project.id)],
    ["READINESS", `${project._applicationReadinessScore || calculateApplicationReadiness(godown)}%`],
    ["PROGRESS", `${godown.constructionOverallProgress ?? 0}%`],
    ["TENDER", godown.tenderAwardVendorName ? "AWARDED" : "PENDING"],
    ["SITE VISIT", clean(godown.siteVisitStatus || "NEW")],
    ["STATUS", clean(project.stage || "ON TRACK")],
  ];

  kpis.forEach((k, i) => {
    const x = 32 + i * (kpiW + 8);

    pdf.setDrawColor(...BORDER);
    pdf.roundedRect(x, kpiY, kpiW, 76, 8, 8, "S");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6);
    pdf.setTextColor(...MUTED);
    pdf.text(k[0], x + kpiW / 2, kpiY + 17, { align: "center" });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...GREEN);
    pdf.text(k[1], x + kpiW / 2, kpiY + 43, {
      align: "center",
      maxWidth: kpiW - 8,
    });
  });

  y += 105;
  sectionHeader("PROJECT TIMELINE");

  const lineY = y + 28;
  pdf.setDrawColor(...GREEN);
  pdf.line(60, lineY, pageW - 60, lineY);

  const points = [
    ["Application", formatDate(godown.createdAt, false)],
    ["Scrutiny", "—"],
    ["Tender", godown.startAt ? formatDate(godown.startAt, false) : "—"],
    ["Site Visit", formatDate(godown.siteVisitPlanDate, false)],
    ["LOA Issued", clean(godown.loaGenerateDate)],
    ["Construction", "In Progress"],
  ];

  points.forEach((p, i) => {
    const x = 60 + i * ((pageW - 120) / (points.length - 1));
    pdf.setFillColor(...GREEN);
    pdf.circle(x, lineY, 6, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.3);
    pdf.setTextColor(...TEXT);
    pdf.text(p[0], x, lineY + 24, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.6);
    pdf.setTextColor(...MUTED);
    pdf.text(p[1], x, lineY + 37, { align: "center" });
  });

  y += 88;

  const colW = (pageW - 64 - 16) / 3;
  const y1 = y;

  const h1 = infoTable(32, y1, colW, [
    ["Registration No.", clean(godown.pacsRegistrationNo)],
    ["Contact Person", clean(godown.contactPerson || godown.pacsName)],
    ["Mobile", clean(godown.contactPhone)],
    ["Email", clean(godown.contactEmail)],
    ["Location", `${clean(project.locationVillage || godown.village)}, ${clean(project.taluka || godown.taluka)}, ${clean(project.district || godown.district)}`],
  ], "APPLICATION DETAILS");

  const h2 = infoTable(32 + colW + 8, y1, colW, [
    ["Reserve Fund", formatPDFINR(fin.reserveFund)],
    ["Building Fund", formatPDFINR(fin.buildingFund)],
    ["Share Capital", formatPDFINR(fin.shareCapital)],
    ["Avg. Profit", formatPDFINR(fin?.totals?.averageProfit)],
    ["Documents", String(project._applicationDocCount || getAllGodownDocumentCount(godown))],
  ], "FINANCIAL STRENGTH");

  const h3 = infoTable(32 + (colW + 8) * 2, y1, colW, [
    ["Project Type", clean(project.godownType || godown.godownType || "New Godown")],
    ["Capacity", `${clean(project.capacityMt || godown.capacityMt)} MT`],
    ["Estimated Amount", formatPDFINR(godown.amount || godown.baseAmount)],
    ["Award Amount", formatPDFINR(godown.tenderAwardAmount)],
    ["Duration", godown.workDurationDays ? `${godown.workDurationDays} Days` : "—"],
  ], "PROJECT OVERVIEW");

  y = Math.max(h1, h2, h3) + 20;

  /* ================= WORKFLOW TASK STATUS DETAILS ================= */

if (tasks.length > 0) {
  pdf.addPage();
  pageHeader("WORKFLOW TASK STATUS DETAILS");

  const sortedTasks = [...tasks].sort(
    (a, b) =>
      tsValue(a.assignedAt || a.createdAt) -
      tsValue(b.assignedAt || b.createdAt)
  );

  autoTable(pdf, {
    head: [[
      "Sr.",
      "Workflow Step",
      "Task Title / Type",
      "Status",
      "Assigned Date",
      "Completed Date",
      "DPR File",
      "Admin Remark",
    ]],

    body: sortedTasks.map((t, idx) => {
      const step =
        t.workflowStep ||
        t.stage ||
        t.phase ||
        t.category ||
        t.type ||
        "-";

      const title =
        t.title ||
        t.taskTitle ||
        t.activity ||
        t.type ||
        "-";

      const isDpr = String(step).toUpperCase() === "DPR_CA";

      const rawDocs =
        t.files ||
        t.docs ||
        t.attachments ||
        t.documents ||
        [];

      const docsArr = Array.isArray(rawDocs) ? rawDocs : [];

      const hasDpr = isDpr && (docsArr.length > 0 || t.fileUrl);

      return [
        String(idx + 1),
        step,
        title,
        normalStatus(t.status || "PENDING"),
        formatDate(t.assignedAt),
        formatDate(t.completedAt),
        hasDpr ? "Download DPR" : "-",
        t.adminDecision ||
          t.decisionRemark ||
          t.approvalRemark ||
          t.adminReason ||
          t.remark ||
          "-",
      ];
    }),

    startY: y,
    theme: "grid",
    margin: { left: 28, right: 28 },

    styles: {
      fontSize: 6.4,
      cellPadding: 4,
      textColor: [0, 0, 0],
      lineColor: [170, 170, 170],
      lineWidth: 0.35,
      valign: "top",
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      fontSize: 6.5,
    },

    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },

    columnStyles: {
      0: { cellWidth: 24, halign: "center" },
      1: { cellWidth: 70 },
      2: { cellWidth: 85 },
      3: { cellWidth: 62, halign: "center", fontStyle: "bold" },
      4: { cellWidth: 76 },
      5: { cellWidth: 76 },
      6: { cellWidth: 70, halign: "center", fontStyle: "bold" },
      7: { cellWidth: 102 },
    },

    didParseCell: (data) => {
      if (data.section !== "body") return;

      if (data.column.index === 3) {
        const status = String(data.cell.raw || "").toUpperCase();

        if (status === "COMPLETED") {
          data.cell.styles.fillColor = [220, 252, 231];
          data.cell.styles.textColor = [0, 0, 0];
        } else if (status === "IN_PROGRESS") {
          data.cell.styles.fillColor = [219, 234, 254];
          data.cell.styles.textColor = [0, 0, 0];
        } else if (status === "ASSIGNED" || status === "PENDING") {
          data.cell.styles.fillColor = [254, 243, 199];
          data.cell.styles.textColor = [0, 0, 0];
        }
      }

      if (data.column.index === 6 && data.cell.raw === "Download DPR") {
        data.cell.styles.fillColor = [220, 252, 231];
        data.cell.styles.textColor = [0, 0, 0];
        data.cell.styles.fontStyle = "bold";
      }
    },

    didDrawCell: (data) => {
      if (data.section !== "body") return;
      if (data.column.index !== 6) return;

      const task = sortedTasks[data.row.index];
      if (!task) return;

      const step =
        task.workflowStep ||
        task.stage ||
        task.phase ||
        task.category ||
        task.type ||
        "";

      if (String(step).toUpperCase() !== "DPR_CA") return;

      const rawDocs =
        task.files ||
        task.docs ||
        task.attachments ||
        task.documents ||
        [];

      const docsArr = Array.isArray(rawDocs) ? rawDocs : [];

      const file =
        docsArr[0] ||
        (task.fileUrl ? { url: task.fileUrl } : null);

      const url =
        file?.url ||
        file?.link ||
        file?.fileUrl ||
        "";

      if (!url) return;

      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.textWithLink(
        "Download DPR",
        data.cell.x + data.cell.width / 2,
        data.cell.y + 13,
        {
          url,
          align: "center",
        }
      );
    },
  });

  y = pdf.lastAutoTable.finalY + 20;
}

  

  /* ================= TENDER PAGE ================= */
  pdf.addPage();
  pageHeader("TENDER & CONTRACTOR DETAILS");

  const leftW = pageW - 64 - 160;

  const tEnd = infoTable(32, y, leftW, [
    ["Tender ID", clean(godown.tenderId || godown.linkedTenderId)],
    ["Notice No.", clean(godown.noticeNo || godown.tenderNoticeNo)],
    ["Tender Title", clean(godown.projectName || godown.tenderTitle)],
    ["Tender Type", clean(godown.tenderType)],
    ["Tender Start Date", godown.startAt ? formatDate(godown.startAt) : "—"],
    ["Tender End Date", godown.endAt ? formatDate(godown.endAt) : "—"],
    ["Opening Date", godown.autoOpenAt ? formatDate(godown.autoOpenAt) : "—"],
    ["Estimated Amount", formatPDFINR(godown.amount || godown.baseAmount)],
    ["Award Amount", formatPDFINR(godown.tenderAwardAmount)],
    ["LOA Generate Date", clean(godown.loaGenerateDate)],
    ["Work Start Date", clean(godown.workStartDate)],
  ], "TENDER INFORMATION");

  pdf.setFillColor(...LIGHT);
  pdf.roundedRect(32 + leftW + 12, y + 22, 148, 150, 8, 8, "F");

  pdf.setFont("helvetica", "bold");
pdf.setFontSize(22);
pdf.setTextColor(...GREEN);
pdf.text("AWARD", 32 + leftW + 86, y + 78, { align: "center" });

pdf.setFontSize(16);
pdf.text("DONE", 32 + leftW + 86, y + 105, { align: "center" });

pdf.setFontSize(8);
pdf.setTextColor(...MUTED);
pdf.text("Tender Finalized", 32 + leftW + 86, y + 128, { align: "center" });

  y = tEnd + 12;
  pdf.addPage();
pageHeader("TENDER PARTICIPATION ANALYSIS");

  const tenderApps = Array.isArray(godown.tenderApplications)
  ? godown.tenderApplications
  : [];

const getAppliedDate = (a) =>
  a?.submittedAt ||
  a?.appliedAt ||
  a?.createdAt ||
  "";

const getBidAmount = (a) => {
  const base = Number(godown.amount || godown.baseAmount || 0);

  if (a?.effectiveAmount !== undefined && a?.effectiveAmount !== null) {
    return Number(a.effectiveAmount || 0);
  }

  if (a?.quotedAmount !== undefined && a?.quotedAmount !== null) {
    return Number(a.quotedAmount || 0);
  }

  const delta = Number(a?.deltaAmount || 0);
  const type = String(a?.selectType || "").toUpperCase();

  if (type === "ABOVE") return base + delta;
  if (type === "BELOW") return base - delta;

  return base;
};

const rankedTenderApps = tenderApps
  .map((a) => ({
    ...a,
    bidAmount: getBidAmount(a),
  }))
  .sort((a, b) => Number(a.bidAmount || 0) - Number(b.bidAmount || 0))
  .map((a, i) => ({
    ...a,
    rank: i === 0 ? "L1" : i === 1 ? "L2" : i === 2 ? "L3" : String(i + 1),
  }));

if (rankedTenderApps.length > 0) {
 

  autoTable(pdf, {
    head: [[
      "Sr",
      "Vendor Name",
      "City",
      "Quoted Amount",
      "+/-",
      "Applied Date",
      "Rank",
    ]],
    body: rankedTenderApps.map((a, i) => [
      String(i + 1),
      clean(a.vendorName || a.vendorCompany || a.companyName || a.vendorUid),
      clean(a.city || a.district),
      formatPDFINR(a.bidAmount),
      clean(a.selectType || a.percentAboveBelow || "—"),
      formatDate(getAppliedDate(a)),
      a.rank,
    ]),
    startY: y + 22,
    theme: "grid",
    margin: { left: 32, right: 32 },
    styles: {
      fontSize: 6.5,
      cellPadding: 4,
      valign: "top",
      lineColor: BORDER,
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: GREEN,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 145 },
      2: { cellWidth: 58 },
      3: { cellWidth: 82 },
      4: { cellWidth: 50 },
      5: { cellWidth: 92 },
      6: { cellWidth: 45 },
    },
  });

  y = pdf.lastAutoTable.finalY + 14;
} else {
  infoTable(32, y, pageW - 64, [
    ["Total Participated Vendors", "0"],
    ["Remark", "No tender participation records found."],
  ], "TENDER PARTICIPATION ANALYSIS");

  y = pdf.lastAutoTable.finalY + 40;
}

  pdf.setFont("helvetica", "bold");
pdf.setFontSize(12);
pdf.setTextColor(...GREEN);
pdf.text("CONTRACTOR / SITE / PMC DECISION", 32, y);

pdf.setDrawColor(...GOLD);
pdf.line(32, y + 8, pageW - 32, y + 8);

y += 24;

infoTable(32, y, pageW - 64, [
  ["Contractor / Vendor", clean(godown.contractorName || godown.tenderAwardVendorName)],
  ["Contractor Mobile", clean(godown.contractorMobile)],
  ["Contractor Email", clean(godown.contractorEmail)],
  ["Site Visit Status", clean(godown.siteVisitStatus || "NEW")],
  ["Site Engineer", clean(godown.siteVisitAssignedTo)],
  ["PMC Decision", clean(godown.pmcDecision)],
  ["PMC Remark", clean(godown.pmcDecisionRemark)],
]);

  /* ================= CONSTRUCTION OVERVIEW ================= */
  pdf.addPage();
  pageHeader("CONSTRUCTION PROGRESS OVERVIEW");

  autoTable(pdf, {
    head: [["STAGE", "DESCRIPTION", "STATUS", "START DATE", "COMPLETE DATE", "REMARK"]],
    body: CONSTRUCTION_TIMELINE_STAGES.map((st, i) => {
      const x = timeline[st.key] || {};
      return [
        String(i + 1),
        st.label,
        clean(x.status || "PENDING"),
        clean(x.startDate || x.date),
        clean(x.completeDate || x.endDate),
        clean(x.note || x.remark),
      ];
    }),
    startY: y,
    theme: "grid",
    margin: { left: 32, right: 32 },
    styles: { fontSize: 6.7, cellPadding: 5, valign: "top" },
    headStyles: { fillColor: GREEN, textColor: [255, 255, 255] },
  });

  y = pdf.lastAutoTable.finalY + 22;

  infoTable(32, y, (pageW - 76) / 2, [
    ["Overall Completion", `${godown.constructionOverallProgress ?? 0}%`],
    [
      "Completed Stages",
      String(
        CONSTRUCTION_TIMELINE_STAGES.filter((s) =>
          clean(timeline[s.key]?.status).toUpperCase().includes("COMPLETE")
        ).length
      ),
    ],
    [
      "Pending Stages",
      String(
        CONSTRUCTION_TIMELINE_STAGES.filter(
          (s) => !clean(timeline[s.key]?.status).toUpperCase().includes("COMPLETE")
        ).length
      ),
    ],
  ], "PROGRESS SUMMARY");

  infoTable(32 + (pageW - 76) / 2 + 12, y, (pageW - 76) / 2, [
    ["Observation 1", "Stage-wise work is monitored through uploaded photo evidence."],
    ["Observation 2", "Progress details are based on contractor dashboard updates."],
    ["Observation 3", "PMC/stakeholder can verify work status from this report."],
  ], "KEY OBSERVATIONS");

  /* ================= STAGE PHOTO PAGES ================= */
 /* ================= STAGE PHOTO PAGES ================= */

for (const st of CONSTRUCTION_TIMELINE_STAGES) {
  const stageData = timeline[st.key] || {};
  const images = stageFiles(stageData).filter((f) =>
    String(f.type || f.name).toLowerCase().includes("image")
  );

  if (!images.length) continue;

  let imgIndex = 0;

  while (imgIndex < images.length) {
    pdf.addPage();
    pageHeader("CONSTRUCTION STAGE PHOTOS");

    pdf.setFillColor(...GREEN);
    pdf.rect(32, y, pageW - 64, 26, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.text(st.label, 42, y + 17);

    statusBadge(pageW - 125, y + 4, stageData.status);

    y += 36;

    const metaEndY = infoTable(32, y, pageW - 64, [
      ["Start Date", clean(stageData.startDate || stageData.date)],
      ["Completion Date", clean(stageData.completeDate || stageData.endDate)],
      ["Remark", clean(stageData.note || stageData.remark)],
    ]);

    y = metaEndY + 16;

    const remaining = images.length - imgIndex;
    const pageImages = images.slice(imgIndex, imgIndex + 6);

    let cols = 2;
    let imgW = (pageW - 76) / 2;
    let imgH = 150;

    if (pageImages.length >= 5) {
      cols = 3;
      imgW = (pageW - 88) / 3;
      imgH = 118;
    }

    const gap = 12;

    for (let i = 0; i < pageImages.length; i += 1) {
      const col = i % cols;
      const row = Math.floor(i / cols);

      const xx = 32 + col * (imgW + gap);
      const yy = y + row * (imgH + 34);

      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(...BORDER);
      pdf.roundedRect(xx, yy, imgW, imgH + 22, 5, 5, "FD");

      const b64 = await imageToBase64(pageImages[i].url);

      if (b64) {
        pdf.addImage(
          b64,
          "JPEG",
          xx + 4,
          yy + 4,
          imgW - 8,
          imgH - 8,
          "",
          "FAST"
        );
      }

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6);
      pdf.setTextColor(...MUTED);
      pdf.text(
        pageImages[i].name || `Photo ${imgIndex + i + 1}`,
        xx + 6,
        yy + imgH + 12,
        { maxWidth: imgW - 12 }
      );
    }

    imgIndex += pageImages.length;
  }
}

  /* ================= CERTIFICATION ================= */
  pdf.addPage();
  pageHeader("CERTIFICATION");

  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(1);
  pdf.roundedRect(32, y, pageW - 64, 270, 8, 8, "S");

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...TEXT);
  pdf.text(
    "This is to certify that the above information is generated from the SAAM Project Monitoring System based on available records, documents, inspections and construction progress updates as on the report date.",
    52,
    y + 38,
    { maxWidth: pageW - 104, lineHeightFactor: 1.45 }
  );

  pdf.text(
    "The project is being monitored regularly and the progress is recorded for stakeholder scrutiny, PMC review and official record purposes.",
    52,
    y + 102,
    { maxWidth: pageW - 104, lineHeightFactor: 1.45 }
  );

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...GREEN);
  pdf.text("Nareshbhai P. Chaudhary", 52, y + 178);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...TEXT);
  pdf.text("CEO & Founder", 52, y + 194);
  pdf.text("Shine Agri Allied And Marketing", 52, y + 210);
  pdf.text("Multistate Cooperative Society Limited", 52, y + 226);

  pdf.setFillColor(...GREEN);
  pdf.circle(pageW - 95, y + 185, 34, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text("SAAM", pageW - 95, y + 190, { align: "center" });

  y += 310;

  infoTable(32, y, pageW - 64, [
    ["Report Generated By", "SAAM Project Monitoring System"],
    ["Generated On", generatedAt],
    ["Report Version", "1.0"],
    ["System Note", "This is a system generated report."],
  ], "REPORT DETAILS");

  pdf.setFillColor(...DARK_GREEN);
  pdf.rect(0, pageH - 66, pageW, 66, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  pdf.text("SAAM Project Monitoring System", 38, pageH - 38);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text("Powered by SAAM | Monitored for Transparency", 38, pageH - 24);

  footer();
  return pdf;
}
function ProjectReportCenter({ selectedProject }) {
  const [reportDate, setReportDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [savingReport, setSavingReport] = useState(false);
  const [reports, setReports] = useState([]);

  const godown = selectedProject._godown || {};
  const reportProjectId = selectedProject.id || godown.id || godown.applicationId || "";

  useEffect(() => {
    if (!reportProjectId) return undefined;

    const qRef = query(
      collection(db, "stakeholder_project_reports"),
      where("projectId", "==", reportProjectId)
    );

    const unsub = onSnapshot(qRef, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => tsValue(b.createdAt) - tsValue(a.createdAt));

      setReports(rows);
    });

    return () => unsub();
  }, [reportProjectId]);

  const downloadPdf = async (recordAfterSave = true) => {
    if (!selectedProject) {
      toast.error("Project not found.");
      return;
    }

    try {
      setSavingReport(true);

      const pdf = await generateDetailedProjectReportPdf(selectedProject, reportDate);
      const fileName = makeReportFileName(selectedProject, reportDate);

      pdf.save(fileName);

      if (recordAfterSave) {
        await addDoc(collection(db, "stakeholder_project_reports"), {
          projectId: reportProjectId,
          applicationId: godown.applicationId || "",
          pacsName: selectedProject.pacsName || godown.pacsName || "",
          district: selectedProject.district || godown.district || "",
          reportDate,
          fileName,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || "",
          createdByEmail: auth.currentUser?.email || "",
          status: "GENERATED",
          mailTo: "tender@shineagri.org",
          mailStatus: "QUEUED",
          mailNote:
            "PDF browser me download hua hai. Email auto-send ke liye backend/Firestore Email extension ko is record/mail_queue se trigger karein.",
        });

        await addDoc(collection(db, "mail_queue"), {
          to: ["tender@shineagri.org"],
          message: {
            subject: `Project Report Generated - ${
              selectedProject.pacsName || godown.applicationId || reportProjectId
            }`,
            text: `Project detailed report generated.

PACS: ${selectedProject.pacsName || "—"}
Application ID: ${godown.applicationId || "—"}
District: ${selectedProject.district || godown.district || "—"}
Report Date: ${reportDate}
Generated By: ${auth.currentUser?.email || "Stakeholder"}

Note: PDF was downloaded in browser. Use stakeholder_project_reports record for report log.`,
          },
          projectId: reportProjectId,
          applicationId: godown.applicationId || "",
          createdAt: serverTimestamp(),
          status: "QUEUED",
        });
      }

      toast.success("Report PDF downloaded and record saved. PMC mail queued.");
    } catch (e) {
      console.error(e);
      toast.error("Report generate failed.");
    } finally {
      setSavingReport(false);
    }
  };

  const reDownloadRecord = async (r) => {
    const pdf = await generateDetailedProjectReportPdf(
  selectedProject,
  r.reportDate || reportDate
);
    pdf.save(r.fileName || makeReportFileName(selectedProject, r.reportDate));
  };

  const totalFiles = CONSTRUCTION_TIMELINE_STAGES.reduce((sum, st) => {
    const x = godown.constructionTimeline?.[st.key] || {};
    return sum + getReportFileUrls(x).length;
  }, 0);

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-700">
              Project Report Center
            </div>
            <div className="mt-2 text-xl font-black text-slate-950">
              Detailed date-wise report generate karo
            </div>
            <div className="mt-1 text-sm text-slate-500">
              Application, Site/PMC, Tasks, Tender, Contractor details,
              Construction stages aur uploaded photo/file links PDF me aayenge.
            </div>
          </div>

          <div className="grid gap-3 rounded-3xl border bg-white p-4 shadow-sm md:w-[360px]">
            <label className="text-[11px] font-black uppercase tracking-wider text-slate-500">
              Report Date
            </label>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-black outline-none focus:border-emerald-400"
            />
            <button
              type="button"
              disabled={savingReport}
              onClick={() => downloadPdf(true)}
              className="rounded-full bg-emerald-600 px-5 py-3 text-xs font-black text-white shadow hover:bg-emerald-700 disabled:bg-slate-300"
            >
              {savingReport ? "Generating..." : "Generate & Download PDF"}
            </button>
          </div>
        </div>
      </div>
            <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-3xl border bg-white p-4 shadow-sm">
          <div className="text-xs font-bold text-slate-500">Application ID</div>
          <div className="mt-1 font-black text-slate-950">
            {godown.applicationId || "—"}
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-4 shadow-sm">
          <div className="text-xs font-bold text-slate-500">PACS / Society</div>
          <div className="mt-1 font-black text-slate-950">
            {selectedProject.pacsName || godown.pacsName || "—"}
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-4 shadow-sm">
          <div className="text-xs font-bold text-slate-500">
            Construction Progress
          </div>
          <div className="mt-1 text-2xl font-black text-emerald-700">
            {godown.constructionOverallProgress ?? 0}%
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-4 shadow-sm">
          <div className="text-xs font-bold text-slate-500">
            Uploaded Photos / Files
          </div>
          <div className="mt-1 text-2xl font-black text-emerald-700">
            {totalFiles}
          </div>
        </div>
      </div>

      <div className="rounded-3xl border bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-sm font-black text-slate-950">
              Report Preview Summary
            </div>
            <div className="text-xs text-slate-500">
              PDF report me niche diye gaye sections auto include honge.
            </div>
          </div>

          <Badge color="green">Premium PDF Ready</Badge>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Report Summary</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Report ke start me complete briefing rahegi ki report me kya-kya
              cover kiya gaya hai.
            </div>
          </div>

          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Application Details</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Application ID, PACS name, registration, mobile, email, location,
              capacity aur readiness.
            </div>
          </div>

          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Site / PMC</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Site visit status, engineer, site report, PMC decision aur remark.
            </div>
          </div>

          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Workflow Tasks</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Assigned task, status, assigned date, completed date aur admin
              decision / remark.
            </div>
          </div>

          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Tender & Contractor</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Tender ID, award amount, contractor/vendor name, mobile, email,
              LOA date, start date aur duration.
            </div>
          </div>

          <div className="rounded-2xl border bg-slate-50 p-4">
            <div className="font-black text-slate-900">Construction + Photos</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Stage-wise status, start date, complete date, remark aur uploaded
              photo/file links.
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-sm font-black text-slate-950">
              Generated Report History
            </div>
            <div className="text-xs text-slate-500">
              Record niche table me rahega. Future me same report dobara download
              kar sakte ho.
            </div>
          </div>

          <Badge color="blue">{reports.length} Report(s)</Badge>
        </div>

        {reports.length === 0 ? (
          <EmptyState
            title="No report generated yet"
            note="Date select karke Generate & Download PDF click karo."
          />
        ) : (
          <div className="overflow-hidden rounded-3xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-950 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Report Date</th>
                  <th className="px-4 py-3 text-left">File Name</th>
                  <th className="px-4 py-3 text-left">Generated By</th>
                  <th className="px-4 py-3 text-left">Mail</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-emerald-50/50">
                    <td className="px-4 py-3 font-bold text-slate-800">
                      {r.reportDate
                        ? dayjs(r.reportDate).format("DD/MM/YYYY")
                        : "—"}
                    </td>

                    <td className="px-4 py-3 text-xs font-semibold text-slate-600">
                      {r.fileName || "—"}
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-500">
                      {r.createdByEmail || r.createdBy || "—"}
                      <div className="mt-1 text-[11px]">
                        {formatDate(r.createdAt)}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <Badge color={r.mailStatus === "SENT" ? "green" : "yellow"}>
                        {r.mailStatus || "QUEUED"}
                      </Badge>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {r.mailTo || "tender@shineagri.org"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => reDownloadRecord(r)}
                        className="rounded-full bg-slate-950 px-4 py-2 text-xs font-black text-white hover:bg-slate-800"
                      >
                        Download Again
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">
         
        </div>
      </div>
    </div>
  );
}

function WorkflowTaskTable({ tasks = [] }) {
  if (!tasks.length) {
    return (
      <EmptyState
        title="No workflow tasks yet"
        note="Workflow tasks will appear when PMC/admin creates them."
      />
    );
  }

  const sortedTasks = [...tasks].sort(
    (a, b) =>
      tsValue(a.assignedAt || a.createdAt) -
      tsValue(b.assignedAt || b.createdAt)
  );

  const vendorTypes = ["SURVEY", "SOIL_TEST", "DESIGN", "ESTIMATE", "DPR_CA"];

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="min-w-full text-[12px]">
          <thead className="bg-slate-950 text-white">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Step / Type</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Files</th>
              <th className="px-4 py-3 text-left">Timeline</th>
              <th className="px-4 py-3 text-left">Admin Decision / Remark</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 bg-white">
            {sortedTasks.map((t, i) => {
              const status = normalStatus(t.status);

              const badgeColor =
                status === "COMPLETED"
                  ? "green"
                  : status === "IN_PROGRESS"
                  ? "blue"
                  : status === "ASSIGNED" || status === "PENDING"
                  ? "yellow"
                  : "gray";

              const stepLabel =
                t.workflowStep ||
                t.stage ||
                t.phase ||
                t.category ||
                t.type ||
                "— Task Title —";

              const title = t.title || t.taskTitle || t.activity || "";

              const adminDecision =
                t.adminDecision ||
                t.decisionRemark ||
                t.approvalStatus ||
                t.approvalRemark ||
                t.adminReason ||
                t.remark ||
                "";

              const rawDocs =
                t.files || t.docs || t.attachments || t.documents || [];
              const docsArr = Array.isArray(rawDocs) ? rawDocs : [];
              const singleFileUrl = !docsArr.length && t.fileUrl ? t.fileUrl : "";
              const isVendorStage = vendorTypes.includes(t.type);

              return (
                <tr key={t.id || i} className="hover:bg-emerald-50/40">
                  <td className="px-4 py-3 align-top font-bold text-slate-500">
                    {i + 1}
                  </td>

                  <td className="px-4 py-3 align-top">
                    <div className="font-black text-slate-900">{stepLabel}</div>
                    {title && (
                      <div className="mt-1 text-[11px] text-slate-500">
                        {title}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3 align-top">
                    <Badge color={badgeColor}>{status || "PENDING"}</Badge>
                  </td>

                  <td className="px-4 py-3 align-top">
                    {isVendorStage ? (
                      docsArr.length > 0 || singleFileUrl ? (
                        <span className="text-[11px] text-slate-400">
                          Internal survey / soil / design / estimate / DPR
                          documents – download only by PMC & workflow admin.
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">
                          No files uploaded.
                        </span>
                      )
                    ) : (
                      <div className="space-y-2">
                        {docsArr.length === 0 && !singleFileUrl && (
                          <span className="text-[11px] text-slate-400">
                            No files
                          </span>
                        )}

                        {docsArr.map((d, idxDoc) => (
                          <DocumentLink key={idxDoc} file={d} index={idxDoc} />
                        ))}

                        {docsArr.length === 0 && singleFileUrl && (
                          <a
                            href={singleFileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white"
                          >
                            Download file
                          </a>
                        )}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3 align-top text-[12px] text-slate-600">
                    <div>
                      <b>Assigned:</b> {formatDate(t.assignedAt)}
                    </div>
                    <div>
                      <b>Planned:</b>{" "}
                      {formatDate(t.plannedFor || t.plannedAt) !== "—"
                        ? formatDate(t.plannedFor || t.plannedAt)
                        : formatDate(t.dueDate || t.dueBy)}
                    </div>
                    <div>
                      <b>Completed:</b> {formatDate(t.completedAt)}
                    </div>
                  </td>

                  <td className="px-4 py-3 align-top text-[12px] text-slate-600">
                    {adminDecision || (
                      <span className="text-slate-400">
                        No admin decision / remark
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function ProjectDetailView({ selectedProject, onClose }) {
  const [activeTab, setActiveTab] = useState("application");

  const godown = selectedProject._godown || {};
  const mainProject = selectedProject._projectDashboard || null;

  const tabs = [
    { id: "application", label: "Application", icon: "📄" },
    { id: "documents", label: "Documents", icon: "📁" },
    { id: "financial", label: "Financial", icon: "₹" },
    { id: "site", label: "Site / PMC", icon: "📍" },
    { id: "tasks", label: "Tasks", icon: "✅" },
    { id: "tender", label: "Tender", icon: "📑" },
    { id: "construction", label: "Construction", icon: "🚧" },
    { id: "report", label: "Report", icon: "📊" },
  ];

  const fin = godown.financialDetails || {};
  const profits = Array.isArray(fin.profits) ? fin.profits : [];
  const docGroups = getGodownApplicationDocGroups(godown);
  const missing = getApplicationMissingItems(godown);
  const readiness = calculateApplicationReadiness(godown);

  const constructionTimeline = godown.constructionTimeline || {};
  const overallProgress =
    typeof godown.constructionOverallProgress === "number"
      ? godown.constructionOverallProgress
      : computeConstructionProgressFromTimeline(constructionTimeline);

  const renderContent = () => {
    if (activeTab === "application") {
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-emerald-50 border p-4">
              <div className="text-xs font-bold text-slate-500">Application ID</div>
              <div className="font-black text-slate-900">
                {godown.applicationId || "—"}
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 border p-4">
              <div className="text-xs font-bold text-slate-500">Institution</div>
              <div className="font-black text-slate-900">
                {selectedProject.pacsName || "—"}
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 border p-4">
              <div className="text-xs font-bold text-slate-500">Registration</div>
              <div className="font-black text-slate-900">
                {godown.pacsRegistrationNo || "—"}
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 border p-4">
              <div className="text-xs font-bold text-slate-500">Capacity</div>
              <div className="font-black text-slate-900">
                {selectedProject.capacityMt || "—"} MT
              </div>
            </div>
          </div>

          <div className="rounded-3xl border bg-white p-5">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <b>District:</b> {selectedProject.district || "—"}
              </div>
              <div>
                <b>Taluka:</b> {selectedProject.taluka || "—"}
              </div>
              <div>
                <b>Village:</b> {selectedProject.locationVillage || "—"}
              </div>
              <div>
                <b>Mobile:</b> {godown.contactPhone || "—"}
              </div>
              <div>
                <b>Email:</b> {godown.contactEmail || "—"}
              </div>
              <div>
                <b>Submitted:</b> {formatDate(godown.createdAt)}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs font-black text-emerald-700 uppercase">
                  AI Readiness
                </div>
                <div className="text-4xl font-black text-emerald-700">
                  {readiness}%
                </div>
              </div>
              <div className="w-64">
                <ProgressBar value={readiness} />
              </div>
            </div>
          </div>

          {missing.length > 0 ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-4">
              <div className="font-black text-red-700 mb-2">Missing / Verify</div>
              <div className="flex flex-wrap gap-2">
                {missing.map((m) => (
                  <Badge key={m} color="red">
                    {m}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4 font-bold text-emerald-700">
              Application appears complete for preliminary scrutiny.
            </div>
          )}
        </div>
      );
    }

    if (activeTab === "documents") {
      return docGroups.length ? (
        <div className="grid md:grid-cols-2 gap-4">
          {docGroups.map((group) => (
            <div key={group.title} className="rounded-3xl border bg-slate-50 p-4">
              <div className="flex justify-between mb-3">
                <div className="font-black text-slate-900">{group.title}</div>
                <Badge color="blue">{group.files.length} file(s)</Badge>
              </div>
              <div className="space-y-2">
                {group.files.map((f, i) => (
                  <DocumentLink key={i} file={f} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="No documents uploaded" />
      );
    }

    if (activeTab === "financial") {
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="rounded-2xl border bg-white p-4">
              <div className="text-xs text-slate-500 font-bold">Reserve Fund</div>
              <div className="text-xl font-black">{formatPDFINR(fin.reserveFund)}</div>
            </div>

            <div className="rounded-2xl border bg-white p-4">
              <div className="text-xs text-slate-500 font-bold">Building Fund</div>
              <div className="text-xl font-black">{formatPDFINR(fin.buildingFund)}</div>
            </div>

            <div className="rounded-2xl border bg-white p-4">
              <div className="text-xs text-slate-500 font-bold">Share Capital</div>
              <div className="text-xl font-black">{formatPDFINR(fin.shareCapital)}</div>
            </div>

            <div className="rounded-2xl border bg-white p-4">
              <div className="text-xs text-slate-500 font-bold">Average Profit</div>
              <div className="text-xl font-black">
                {formatPDFINR(fin?.totals?.averageProfit)}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Financial Year</th>
                  <th className="px-4 py-3 text-right">Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {profits.map((p, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-4 py-3 font-bold">{p.fy}</td>
                    <td className="px-4 py-3 text-right font-black">
                      {formatPDFINR(p.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === "project") {
      if (!mainProject) {
        return <EmptyState title="Project dashboard not created yet" />;
      }

      return (
        <div className="space-y-4">
          <div className="rounded-3xl border bg-emerald-50 p-5">
            <div className="flex justify-between">
              <div>
                <div className="text-xs font-black text-emerald-700 uppercase">
                  Current Stage
                </div>
                <div className="text-xl font-black">
                  {mainProject.currentStage || "—"}
                </div>
              </div>
              <div className="text-3xl font-black text-emerald-700">
                {mainProject.progressPercent ?? selectedProject.progress ?? 0}%
              </div>
            </div>
            <div className="mt-4">
              <ProgressBar
                value={mainProject.progressPercent ?? selectedProject.progress}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(Array.isArray(mainProject.stages) ? mainProject.stages : []).map(
              (s) => (
                <StageCard key={s.code || s.name} stage={s} />
              )
            )}
          </div>
        </div>
      );
    }

    if (activeTab === "site") {
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-3xl border bg-white p-5">
            <div className="flex justify-between">
              <div className="font-black">Site Visit</div>
              <Badge color={godown.siteVisitStatus === "DONE" ? "green" : "yellow"}>
                {godown.siteVisitStatus || "NEW"}
              </Badge>
            </div>

            <div className="mt-4 space-y-2 text-sm">
              <div>
                <b>Engineer:</b> {godown.siteVisitAssignedTo || "—"}
              </div>
              <div>
                <b>Date:</b> {formatDate(godown.siteVisitPlanDate, false)}
              </div>
              <div>
                <b>Report:</b> {godown.siteVisitReportText || "—"}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border bg-white p-5">
            <div className="flex justify-between">
              <div className="font-black">PMC Decision</div>
              <Badge
                color={
                  godown.pmcDecision === "APPROVED_TO_WORKFLOW"
                    ? "green"
                    : "yellow"
                }
              >
                {godown.pmcDecision || "PENDING"}
              </Badge>
            </div>

            <div className="mt-4 text-sm">
              <b>Remark:</b> {godown.pmcDecisionRemark || "—"}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "tasks") {
      return <WorkflowTaskTable tasks={selectedProject._tasks || []} />;
    }

    if (activeTab === "tender") {
      return (
        <div className="grid md:grid-cols-4 gap-3">
          <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-slate-500 font-bold">Tender ID</div>
            <div className="font-black">
              {godown.tenderId || godown.linkedTenderId || "—"}
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-slate-500 font-bold">Vendor</div>
            <div className="font-black">
              {godown.tenderAwardVendorName || godown.contractorName || "—"}
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-slate-500 font-bold">Contractor Mobile</div>
            <div className="font-black">
              {godown.contractorMobile ||
                godown.awardedVendorMobile ||
                godown.vendorMobile ||
                "—"}
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-slate-500 font-bold">Award Amount</div>
            <div className="font-black">{formatPDFINR(godown.tenderAwardAmount)}</div>
          </div>

          <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-slate-500 font-bold">Award Date</div>
            <div className="font-black">
              {formatDate(godown.tenderAwardAt, false)}
            </div>
          </div>

          <div className="rounded-2xl border bg-emerald-50 p-4">
            <div className="text-xs text-emerald-700 font-bold">LOA Date</div>
            <div className="font-black">
              {godown.loaGenerateDate || godown.contractorLoaDate || "—"}
            </div>
          </div>

          <div className="rounded-2xl border bg-emerald-50 p-4">
            <div className="text-xs text-emerald-700 font-bold">Work Start Date</div>
            <div className="font-black">{godown.workStartDate || "—"}</div>
          </div>

          <div className="rounded-2xl border bg-emerald-50 p-4">
            <div className="text-xs text-emerald-700 font-bold">Duration</div>
            <div className="font-black">
              {godown.workDurationDays ? `${godown.workDurationDays} Days` : "—"}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "construction") {
      const totalDays = Number(godown.workDurationDays || 0);
      const start = godown.workStartDate ? new Date(godown.workStartDate) : null;
      const today = new Date();

      let pendingDays = "—";

      if (start && totalDays) {
        start.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const usedDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
        const balanceDays = totalDays - usedDays;

        if (balanceDays >= 0) {
          pendingDays = `${balanceDays} Days Left`;
        } else {
          pendingDays = `${Math.abs(balanceDays)} Days Overdue`;
        }
      }

      return (
        <div className="space-y-4">
          <div className="rounded-3xl border bg-slate-50 p-5">
            <div className="flex justify-between">
              <div className="font-black">Overall Construction Progress</div>
              <div className="text-3xl font-black text-emerald-700">
                {overallProgress}%
              </div>
            </div>

            <div className="mt-4">
              <ProgressBar value={overallProgress} />
            </div>

            <div className="mt-4 grid md:grid-cols-4 gap-3">
              <div className="rounded-2xl border bg-white p-4">
                <div className="text-xs font-bold text-slate-500">LOA Date</div>
                <div className="font-black">
                  {godown.loaGenerateDate || godown.contractorLoaDate || "—"}
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-4">
                <div className="text-xs font-bold text-slate-500">Work Start Date</div>
                <div className="font-black">{godown.workStartDate || "—"}</div>
              </div>

              <div className="rounded-2xl border bg-white p-4">
                <div className="text-xs font-bold text-slate-500">Duration</div>
                <div className="font-black">
                  {godown.workDurationDays ? `${godown.workDurationDays} Days` : "—"}
                </div>
              </div>

              <div className="rounded-2xl border bg-amber-50 p-4">
                <div className="text-xs font-bold text-amber-700">Pending Days</div>
                <div className="font-black text-amber-800">{pendingDays}</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {CONSTRUCTION_TIMELINE_STAGES.map((st) => (
              <ConstructionStageCard
                key={st.key}
                stage={st}
                data={constructionTimeline[st.key] || {}}
              />
            ))}
          </div>
        </div>
      );
    }

    if (activeTab === "report") {
      return <ProjectReportCenter selectedProject={selectedProject} />;
    }

    return null;
  };

  return (
    <div className="h-[92vh] rounded-[2rem] border bg-white shadow-2xl overflow-hidden flex flex-col">
      <div className="bg-slate-950 px-5 py-4 flex justify-between items-center">
        <div>
          <div className="text-[11px] font-black text-emerald-300 uppercase tracking-widest">
            Detailed Scrutiny File
          </div>
          <div className="text-lg font-black text-white">
            {selectedProject.pacsName || "Project Details"}
          </div>
          <div className="text-xs text-slate-300">
            {godown.applicationId || selectedProject.id} ·{" "}
            {selectedProject.district || "—"}
          </div>
        </div>

        <button
          onClick={onClose}
          className="rounded-full bg-white px-4 py-2 text-xs font-black text-slate-950"
        >
          Close
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="w-64 border-r bg-slate-50 p-3 overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cx(
                "w-full mb-2 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-black text-left transition",
                activeTab === tab.id
                  ? "bg-emerald-600 text-white shadow"
                  : "bg-white text-slate-700 hover:bg-emerald-50"
              )}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto bg-white p-5">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

/* ======================= MAIN COMPONENT ======================= */

export default function StakeholderDashboard() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [stageFilter, setStageFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");

  const [page, setPage] = useState(1);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectData, setSelectedProjectData] = useState(null);

  const [dark, setDark] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setProfile(null);
        setLoadingProfile(false);
        toast.error("Please login from Stakeholder Portal first.");
        navigate("/agency/portal", { replace: true });
        return;
      }

      try {
        const ref = doc(db, "portal_users", user.uid);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          toast.error("Stakeholder profile not found.");
          setProfile(null);
          navigate("/agency/portal", { replace: true });
        } else {
          setProfile(snap.data());
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load stakeholder profile.");
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    });

    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!profile) {
      setProjects([]);
      setLoadingProjects(false);
      return undefined;
    }

    setLoadingProjects(true);

    const assignedDistricts = profile.assignedDistricts || [];
    const state = profile.state || "Gujarat";

    let wfQuery;
    let mainProjectQuery;

    if (profile.officeType === "BANK" || profile.officeType === "DISTRICT_REG") {
      if (!assignedDistricts.length) {
        setProjects([]);
        setLoadingProjects(false);
        return undefined;
      }

      if (assignedDistricts.length === 1) {
        wfQuery = query(
          collection(db, "wf_projects"),
          where("district", "==", assignedDistricts[0])
        );

        mainProjectQuery = query(
          collection(db, "projects"),
          where("district", "==", assignedDistricts[0])
        );
      } else {
        wfQuery = query(
          collection(db, "wf_projects"),
          where("district", "in", assignedDistricts.slice(0, 10))
        );

        mainProjectQuery = query(
          collection(db, "projects"),
          where("district", "in", assignedDistricts.slice(0, 10))
        );
      }
    } else {
      wfQuery = query(collection(db, "wf_projects"), where("state", "==", state));
      mainProjectQuery = query(collection(db, "projects"), where("state", "==", state));
    }

    let wfRows = [];
    let projectRows = [];
    let taskRows = [];
    let godownRows = [];
    let tenderRows = [];
    let vendorRows = [];
    let tenderApplicationRows = [];

    const buildViews = () => {
      try {
        const workflowIds = new Set(wfRows.map((p) => p.id));

        projectRows.forEach((p) => {
          if (p.workflowProjectId) workflowIds.add(p.workflowProjectId);
          else if (p.id) workflowIds.add(`PROJECT_ONLY__${p.id}`);
        });

        const wfById = {};
        wfRows.forEach((p) => {
          wfById[p.id] = p;
        });

        const projectByWorkflowId = {};
        projectRows.forEach((p) => {
          if (p.workflowProjectId) projectByWorkflowId[p.workflowProjectId] = p;
          else if (p.id) projectByWorkflowId[`PROJECT_ONLY__${p.id}`] = p;
        });

        const tasksByProj = {};
        taskRows.forEach((t) => {
          if (!t.projectId) return;
          if (!tasksByProj[t.projectId]) tasksByProj[t.projectId] = [];
          tasksByProj[t.projectId].push(t);
        });

        const godownByWorkflowId = {};
        const godownByApplicationOnlyId = {};

        godownRows.forEach((g) => {
          if (g.workflowProjectId) {
            godownByWorkflowId[g.workflowProjectId] = g;
          } else if (g.id) {
            godownByApplicationOnlyId[`APPLICATION_ONLY__${g.id}`] = g;
          }
        });

       const tenderByWorkflowId = {};
const tenderById = {};
const tenderApplicationsByTenderId = {};

tenderApplicationRows.forEach((a) => {
  if (!a.tenderId) return;
  if (!tenderApplicationsByTenderId[a.tenderId]) {
    tenderApplicationsByTenderId[a.tenderId] = [];
  }
  tenderApplicationsByTenderId[a.tenderId].push(a);
});

const vendorById = {};
vendorRows.forEach((v) => {
  if (v.id) vendorById[v.id] = v;
  if (v.uid) vendorById[v.uid] = v;
});

        tenderRows.forEach((t) => {
          if (t.id) tenderById[t.id] = t;
          if (t.workflowProjectId) tenderByWorkflowId[t.workflowProjectId] = t;

          if (Array.isArray(t.linkedWorkflowProjectIds)) {
            t.linkedWorkflowProjectIds.forEach((id) => {
              if (id) tenderByWorkflowId[id] = t;
            });
          }

          if (Array.isArray(t.tenderUnits)) {
            t.tenderUnits.forEach((u) => {
              if (u?.workflowProjectId) tenderByWorkflowId[u.workflowProjectId] = t;
            });
          }
        });

        godownRows.forEach((g) => {
          const gDistrict = g.district || "";
          const gState = g.state || "Gujarat";

          const districtScope =
            profile.officeType === "BANK" || profile.officeType === "DISTRICT_REG"
              ? assignedDistricts.includes(gDistrict)
              : true;

          const stateScope =
            profile.officeType === "STATE_REG" ||
            profile.officeType === "CENTRAL_AGENCY" ||
            profile.officeType === "NODAL_AGENCY"
              ? gState === state
              : true;

          if (!districtScope || !stateScope) return;

          if (g.workflowProjectId) workflowIds.add(g.workflowProjectId);
          else if (g.id) workflowIds.add(`APPLICATION_ONLY__${g.id}`);
        });

        const views = Array.from(workflowIds).map((workflowId) => {
          const wf = wfById[workflowId] || {};
          const main = projectByWorkflowId[workflowId] || {};
          const isProjectOnly = String(workflowId).startsWith("PROJECT_ONLY__");
          const isApplicationOnly = String(workflowId).startsWith(
            "APPLICATION_ONLY__"
          );

          const actualWorkflowId = isProjectOnly
            ? main.workflowProjectId || ""
            : workflowId;

          const godownRaw =
            godownByWorkflowId[actualWorkflowId || workflowId] ||
            godownByWorkflowId[main.workflowProjectId] ||
            godownByApplicationOnlyId[workflowId] ||
            null;

          const tenderRaw =
  tenderByWorkflowId[actualWorkflowId || workflowId] ||
  tenderByWorkflowId[godownRaw?.workflowProjectId] ||
  tenderById[godownRaw?.tenderId] ||
  tenderById[godownRaw?.linkedTenderId] ||
  tenderRows.find((t) => t.id === godownRaw?.tenderId) ||
  tenderRows.find((t) => t.id === godownRaw?.linkedTenderId) ||
  tenderRows.find((t) => t.workflowProjectId === actualWorkflowId) ||
  tenderRows.find((t) =>
    Array.isArray(t.linkedWorkflowProjectIds) &&
    t.linkedWorkflowProjectIds.includes(actualWorkflowId)
  ) ||
  tenderRows.find((t) =>
    Array.isArray(t.tenderUnits) &&
    t.tenderUnits.some((u) => u?.workflowProjectId === actualWorkflowId)
  ) ||
  {};

          const tasks = tasksByProj[actualWorkflowId || workflowId] || [];
          const awardedVendorUid =
  tenderRaw.awardedVendorUid ||
  tenderRaw.tenderAwardVendorUid ||
  godownRaw?.tenderAwardVendorUid ||
  godownRaw?.contractorUid ||
  "";

const vendorDoc = vendorById[awardedVendorUid] || {};
const vendorProfile = vendorDoc.profile || {};
const vendorContact = vendorProfile.contact || {};

const contractorMobile =
  godownRaw?.contractorMobile ||
  tenderRaw.contractorMobile ||
  tenderRaw.awardedVendorMobile ||
  tenderRaw.vendorMobile ||
  vendorProfile.contactPhone ||
  vendorProfile.primaryPhone ||
  vendorContact.primaryPhone ||
  vendorContact.phone ||
  vendorContact.mobile ||
  vendorDoc.mobile ||
  vendorDoc.contactNumber ||
  "";

const contractorEmail =
  godownRaw?.contractorEmail ||
  tenderRaw.contractorEmail ||
  tenderRaw.awardedVendorEmail ||
  tenderRaw.vendorEmail ||
  vendorProfile.contactEmail ||
  vendorProfile.email ||
  vendorContact.email ||
  vendorDoc.auth?.email ||
  vendorDoc.email ||
  "";

          const constructionTimeline = normalizeConstructionTimeline(
            godownRaw || {},
            main
          );

          const constructionOverallProgress =
            typeof godownRaw?.constructionOverallProgress === "number"
              ? godownRaw.constructionOverallProgress
              : computeConstructionProgressFromTimeline(constructionTimeline);

          const taskTotal = tasks.length;
          const taskCompleted = tasks.filter(
            (t) => normalStatus(t.status) === "COMPLETED"
          ).length;

          const taskProgress =
            taskTotal === 0 ? 0 : Math.round((taskCompleted / taskTotal) * 100);

          const mainProgress =
            typeof main.progressPercent === "number"
              ? main.progressPercent
              : Array.isArray(main.stages)
              ? computeStagesProgress(main.stages)
              : null;

          const progress =
            typeof mainProgress === "number"
              ? mainProgress
              : constructionOverallProgress > 0
              ? constructionOverallProgress
              : taskProgress;

          const stage = isApplicationOnly
            ? "Not Started"
            : calcProjectStageFromProgress(progress);

          const lastTaskUpdate = latestTimestamp(
            tasks.map(
              (t) => t.updatedAt || t.completedAt || t.assignedAt || t.createdAt
            )
          );

          const lastUpdated = latestTimestamp(
            main.lastUpdated,
            main.projectDashboardSyncedAt,
            wf.updatedAt,
            wf.projectDashboardSyncedAt,
            godownRaw?.updatedAt,
            godownRaw?.createdAt,
            godownRaw?.projectDashboardSyncedAt,
            tenderRaw?.updatedAt,
            tenderRaw?.createdAt,
            lastTaskUpdate
          );

          const applicationDocCount = getAllGodownDocumentCount(godownRaw || {});
          const readinessScore = calculateApplicationReadiness(godownRaw || {});

          return {
            ...wf,
            id: actualWorkflowId || main.id || godownRaw?.id || workflowId,
            title:
              wf.title ||
              main.workflowProjectTitle ||
              main.name ||
              main.pacsName ||
              godownRaw?.applicationId ||
              "Godown Application",
            pacsName:
              wf.pacsName || main.pacsName || main.name || godownRaw?.pacsName || "",
            district: wf.district || main.district || godownRaw?.district || "",
            taluka: wf.taluka || main.taluka || godownRaw?.taluka || "",
            locationVillage:
              wf.locationVillage ||
              main.locationVillage ||
              godownRaw?.village ||
              godownRaw?.locationVillage ||
              "",
            state:
              wf.state || main.state || godownRaw?.state || profile.state || "Gujarat",
            googleMapsUrl:
              wf.googleMapsUrl || main.googleMapsUrl || godownRaw?.googleMapUrl || "",
            capacityMt: wf.capacityMt || main.capacityMt || godownRaw?.capacityMt || "",
            godownType: wf.godownType || main.godownType || godownRaw?.godownType || "",
            workflowStatus:
              wf.status ||
              main.workflowStatus ||
              main.syncStatus ||
              godownRaw?.status ||
              "APPLICATION_SUBMITTED",
            projectDashboardCurrentStage:
              main.currentStage ||
              wf.projectDashboardCurrentStage ||
              (isApplicationOnly ? "Application Submitted" : ""),
            projectDashboardProgressPercent:
              mainProgress ?? wf.projectDashboardProgressPercent ?? "",
            projectDashboardStageLine: getProjectStageLine(main),
            _tasks: tasks,
            _projectDashboard:
              main && (main.workflowProjectId || main.id) ? main : null,
            _godown: godownRaw
              ? {
                  ...godownRaw,
                  tenderId:
                    godownRaw.tenderId ||
                    godownRaw.linkedTenderId ||
                    tenderRaw.id ||
                    "",
                  linkedTenderId: godownRaw.linkedTenderId || tenderRaw.id || "",
                  tenderAwardVendorName:
                    godownRaw.tenderAwardVendorName ||
                    tenderRaw.awardedVendorName ||
                    tenderRaw.vendorName ||
                    "",
                  tenderAwardAmount:
                    godownRaw.tenderAwardAmount ||
                    tenderRaw.awardedAmount ||
                    tenderRaw.awardedValue ||
                    tenderRaw.amount ||
                    0,
                  loaGenerateDate:
                    godownRaw.loaGenerateDate || tenderRaw.loaGenerateDate || "",
                  contractorLoaDate:
                    godownRaw.contractorLoaDate ||
                    tenderRaw.loaGenerateDate ||
                    tenderRaw.workStartDate ||
                    "",
                  workStartDate:
                    godownRaw.workStartDate ||
                    tenderRaw.workStartDate ||
                    tenderRaw.loaGenerateDate ||
                    "",
                 workDurationDays:
  godownRaw.workDurationDays || tenderRaw.workDurationDays || 0,

startAt:
  tenderRaw.startAt ||
  tenderRaw.tenderStartDate ||
  "",

endAt:
  tenderRaw.endAt ||
  tenderRaw.tenderEndDate ||
  "",

autoOpenAt:
  tenderRaw.autoOpenAt ||
  tenderRaw.tenderOpenDate ||
  "",

noticeNo:
  tenderRaw.noticeNo ||
  tenderRaw.ifbNo ||
  tenderRaw.tenderNoticeNo ||
  "",

tenderType:
  tenderRaw.tenderType ||
  tenderRaw.type ||
  "Open",

projectName:
  tenderRaw.projectName ||
  tenderRaw.title ||
  tenderRaw.projectTitle ||
  "",

amount:
  Number(
    tenderRaw.amount ||
    tenderRaw.baseAmount ||
    tenderRaw.estimatedCost ||
    tenderRaw.estimatedValue ||
    0
  ),
                  contractorUid:
                    godownRaw.contractorUid || tenderRaw.awardedVendorUid || "",
                  contractorMobile:
                    godownRaw.contractorMobile ||
                    tenderRaw.contractorMobile ||
                    tenderRaw.awardedVendorMobile ||
                    tenderRaw.vendorMobile ||
                    "",
                  contractorEmail:
                    godownRaw.contractorEmail ||
                    tenderRaw.contractorEmail ||
                    tenderRaw.awardedVendorEmail ||
                    tenderRaw.vendorEmail ||
                    "",
                  constructionTimeline,
                  tenderApplications:
  tenderApplicationsByTenderId[tenderRaw.id] ||
  tenderApplicationsByTenderId[godownRaw?.tenderId] ||
  tenderApplicationsByTenderId[godownRaw?.linkedTenderId] ||
  [],
                  constructionOverallProgress,
                }
              : {
                  tenderId: tenderRaw.id || "",
                  linkedTenderId: tenderRaw.id || "",
                  tenderAwardVendorName:
                    tenderRaw.awardedVendorName || tenderRaw.vendorName || "",
                  tenderAwardAmount:
                    tenderRaw.awardedAmount ||
                    tenderRaw.awardedValue ||
                    tenderRaw.amount ||
                    0,
                  loaGenerateDate: tenderRaw.loaGenerateDate || "",
                  contractorLoaDate:
                    tenderRaw.loaGenerateDate || tenderRaw.workStartDate || "",
                  workStartDate:
                    tenderRaw.workStartDate || tenderRaw.loaGenerateDate || "",
                  workDurationDays: tenderRaw.workDurationDays || 0,
                  startAt: tenderRaw.startAt || "",
endAt: tenderRaw.endAt || "",
autoOpenAt: tenderRaw.autoOpenAt || "",
noticeNo: tenderRaw.noticeNo || tenderRaw.ifbNo || "",
tenderType: tenderRaw.tenderType || "",
projectName: tenderRaw.projectName || tenderRaw.title || "",
amount: tenderRaw.amount || tenderRaw.baseAmount || tenderRaw.estimatedCost || 0,
                  contractorName:
                    tenderRaw.awardedVendorName || tenderRaw.vendorName || "",
                  contractorUid: tenderRaw.awardedVendorUid || "",
                  contractorMobile:
                    tenderRaw.contractorMobile ||
                    tenderRaw.awardedVendorMobile ||
                    tenderRaw.vendorMobile ||
                    "",
                  contractorEmail:
                    tenderRaw.contractorEmail ||
                    tenderRaw.awardedVendorEmail ||
                    tenderRaw.vendorEmail ||
                    "",
                  constructionTimeline,
                  constructionOverallProgress,
                },
            _isApplicationOnly: isApplicationOnly,
            _applicationDocCount: applicationDocCount,
            _applicationReadinessScore: readinessScore,
            progress,
            stage,
            lastUpdated,
          };
        });

        views.sort((a, b) => tsValue(b.lastUpdated) - tsValue(a.lastUpdated));

        setProjects(views);
        setLoadingProjects(false);
      } catch (e) {
        console.error("[StakeholderDashboard buildViews] error", e);
        setLoadingProjects(false);
      }
    };
        const unsubWf = onSnapshot(wfQuery, (snap) => {
      wfRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      buildViews();
    });

    const unsubProjects = onSnapshot(mainProjectQuery, (snap) => {
      projectRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      buildViews();
    });

    const unsubTasks = onSnapshot(collection(db, "wf_project_tasks"), (snap) => {
      taskRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      buildViews();
    });

    const unsubGodown = onSnapshot(collection(db, "godown_projects"), (snap) => {
      godownRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      buildViews();
    });

    const unsubTenders = onSnapshot(collection(db, "tenders"), (snap) => {
      tenderRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      buildViews();
    });
    const unsubVendors = onSnapshot(collection(db, "vendors"), (snap) => {
  vendorRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  buildViews();
});
const unsubTenderApplications = onSnapshot(
  collection(db, "tender_applications"),
  (snap) => {
    tenderApplicationRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    buildViews();
  }
);

    return () => {
      unsubWf();
      unsubProjects();
      unsubTasks();
      unsubGodown();
      unsubTenders();
      unsubVendors();
      unsubTenderApplications();
    };
  }, [profile]);

  const assignedDistrictsMemo = profile?.assignedDistricts || [];
  const officeType = profile?.officeType || "";

  const stageOptions = [
    "all",
    "Not Started",
    "In Progress",
    "Near Completion",
    "Completed",
  ];

  const allDistrictOptions = useMemo(() => {
    if (!profile) return ["all"];

    if (
      officeType === "STATE_REG" ||
      officeType === "CENTRAL_AGENCY" ||
      officeType === "NODAL_AGENCY"
    ) {
      const set = new Set();
      projects.forEach((p) => p.district && set.add(p.district));
      return ["all", ...Array.from(set)];
    }

    return ["all", ...assignedDistrictsMemo];
  }, [profile, officeType, assignedDistrictsMemo, projects]);

  const filtered = useMemo(() => {
    let list = [...projects];

    if (districtFilter !== "all") {
      list = list.filter((p) => (p.district || "") === districtFilter);
    }

    if (stageFilter !== "all") {
      list = list.filter((p) => p.stage === stageFilter);
    }

    if (typeFilter === "application") {
      list = list.filter((p) => p._isApplicationOnly);
    } else if (typeFilter === "workflow") {
      list = list.filter((p) => !p._isApplicationOnly);
    } else if (typeFilter === "withDocs") {
      list = list.filter((p) => Number(p._applicationDocCount || 0) > 0);
    }

    const needle = q.trim().toLowerCase();

    if (needle) {
      list = list.filter((p) => {
        const godown = p._godown || {};
        const hay = [
          p.pacsName,
          p.title,
          p.locationVillage,
          p.taluka,
          p.district,
          p.id,
          p.projectDashboardCurrentStage,
          p.workflowStatus,
          godown.applicationId,
          godown.pacsRegistrationNo,
          godown.contactPhone,
          godown.contactEmail,
          godown.tenderId,
          godown.contractorName,
          godown.contractorMobile,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [projects, districtFilter, stageFilter, typeFilter, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PER_PAGE;
  const endIdx = startIdx + PER_PAGE;
  const pageItems = filtered.slice(startIdx, endIdx);

  const totals = useMemo(() => {
    const total = filtered.length;
    const notStarted = filtered.filter((p) => p.stage === "Not Started").length;
    const completed = filtered.filter((p) => p.stage === "Completed").length;
    const nearCompletion = filtered.filter((p) => p.stage === "Near Completion").length;
    const inProgress = total - notStarted - completed - nearCompletion;

    const applicationOnly = filtered.filter((p) => p._isApplicationOnly).length;
    const totalDocs = filtered.reduce(
      (sum, p) => sum + Number(p._applicationDocCount || 0),
      0
    );

    const avgReadiness = filtered.length
      ? Math.round(
          filtered.reduce(
            (sum, p) => sum + Number(p._applicationReadinessScore || 0),
            0
          ) / filtered.length
        )
      : 0;

    const highReadiness = filtered.filter(
      (p) => Number(p._applicationReadinessScore || 0) >= 80
    ).length;

    return {
      total,
      notStarted,
      inProgress,
      nearCompletion,
      completed,
      applicationOnly,
      totalDocs,
      avgReadiness,
      highReadiness,
    };
  }, [filtered]);

  const doLogout = async () => {
    await signOut(auth);
    toast("Logged out");
    navigate("/agency/portal", { replace: true });
  };

  const resetFilters = () => {
    setDistrictFilter("all");
    setStageFilter("all");
    setTypeFilter("all");
    setQ("");
    setPage(1);
    setSelectedProjectId(null);
  };

  const handleChangeDistrict = (val) => {
    setDistrictFilter(val);
    setPage(1);
    setSelectedProjectId(null);
  };

  const handleChangeStage = (val) => {
    setStageFilter(val);
    setPage(1);
    setSelectedProjectId(null);
  };

  const handleChangeType = (val) => {
    setTypeFilter(val);
    setPage(1);
    setSelectedProjectId(null);
  };

  const handleChangeSearch = (val) => {
    setQ(val);
    setPage(1);
    setSelectedProjectId(null);
  };

  const exportToExcel = () => {
    if (!filtered.length) {
      toast.error("No projects to export.");
      return;
    }

    const headers = [
      "Workflow / Application ID",
      "Application ID",
      "PACS Name",
      "Registration No",
      "Mobile",
      "Email",
      "Project Title",
      "District",
      "Taluka",
      "Village",
      "State",
      "Capacity (MT)",
      "Godown Type",
      "Workflow Status",
      "Project Dashboard Stage",
      "Overall Stage",
      "Overall Progress (%)",
      "Application Readiness (%)",
      "Application Documents Count",
      "Reserve Fund",
      "Building Fund",
      "Share Capital",
      "Average Profit",
      "Last Update",
      "Tender ID",
      "Tender Award Vendor",
      "Tender Award Amount",
      "Contractor Name",
      "Contractor Mobile",
      "Contractor Email",
      "Contractor LOA Date",
      "Work Start Date",
      "Work Duration Days",
      "Construction Overall Progress (%)",
      ...CONSTRUCTION_TIMELINE_STAGES.flatMap((st) => [
        `${st.label} Status`,
        `${st.label} Start Date`,
        `${st.label} Complete Date`,
        `${st.label} Note`,
        `${st.label} Files`,
      ]),
      "Workflow Tasks Summary",
    ];

    const rows = filtered.map((p) => {
      const godown = p._godown || {};
      const fin = godown.financialDetails || {};
      const constructionTimeline = godown.constructionTimeline || {};

      const constructionCols = CONSTRUCTION_TIMELINE_STAGES.flatMap((st) => {
        const x = constructionTimeline[st.key] || {};
        const files = Array.isArray(x.files) ? x.files : [];

        return [
          x.status || "Pending",
          x.startDate || x.date || "",
          x.completeDate || x.endDate || "",
          x.note || x.remark || "",
          files.length,
        ];
      });

      const taskSummary = (p._tasks || [])
        .map((t) => `${t.type || t.workflowStep || "TASK"}: ${t.status || "PENDING"}`)
        .join(" | ");

      return [
        p.id || "",
        godown.applicationId || "",
        p.pacsName || "",
        godown.pacsRegistrationNo || "",
        godown.contactPhone || "",
        godown.contactEmail || "",
        p.title || "",
        p.district || "",
        p.taluka || "",
        p.locationVillage || "",
        p.state || "",
        p.capacityMt || "",
        p.godownType || "",
        p.workflowStatus || "",
        p.projectDashboardStageLine || "",
        p.stage || "",
        String(p.progress ?? ""),
        p._applicationReadinessScore ?? "",
        p._applicationDocCount ?? "",
        fin.reserveFund || "",
        fin.buildingFund || "",
        fin.shareCapital || "",
        fin?.totals?.averageProfit || "",
        formatDate(p.lastUpdated),
        godown.tenderId || "",
        godown.tenderAwardVendorName || "",
        godown.tenderAwardAmount || "",
        godown.contractorName || godown.tenderAwardVendorName || "",
        godown.contractorMobile || "",
        godown.contractorEmail || "",
        formatDate(godown.contractorLoaDate),
        godown.workStartDate || "",
        godown.workDurationDays || "",
        godown.constructionOverallProgress ?? "",
        ...constructionCols,
        taskSummary,
      ];
    });

    const escapeCell = (val) => {
      const s = String(val ?? "");
      if (s.includes('"') || s.includes(",") || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const csv =
      headers.map(escapeCell).join(",") +
      "\n" +
      rows.map((r) => r.map(escapeCell).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "stakeholder-premium-merged-dashboard-report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!filtered.length) {
      toast.error("No projects to export.");
      return;
    }

    const docPdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    docPdf.setFillColor(248, 250, 252);
    docPdf.rect(
      0,
      0,
      docPdf.internal.pageSize.getWidth(),
      docPdf.internal.pageSize.getHeight(),
      "F"
    );

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(16);
    docPdf.setTextColor(6, 95, 70);
    docPdf.text("Stakeholder Premium Project & Application Report", 30, 34);

    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(9);
    docPdf.setTextColor(80);
    docPdf.text("Generated on: " + dayjs().format("DD/MM/YYYY, hh:mm A"), 30, 50);

    autoTable(docPdf, {
      head: [
        [
          "Application / Project",
          "Location",
          "Application + Documents",
          "Workflow + Project Dashboard",
          "Site Visit / PMC / Tender",
          "Contractor + Construction Timeline",
        ],
      ],
      body: filtered.map((p) => {
        const godown = p._godown || {};
        return [
          [
            p.pacsName || "",
            godown.applicationId ? `Application ID: ${godown.applicationId}` : "",
            `ID: ${p.id || "—"}`,
            godown.pacsRegistrationNo ? `Reg: ${godown.pacsRegistrationNo}` : "",
            godown.contactPhone ? `Mobile: ${godown.contactPhone}` : "",
          ]
            .filter(Boolean)
            .join("\n"),
          [
            p.district ? `District: ${p.district}` : "",
            p.taluka ? `Taluka: ${p.taluka}` : "",
            p.locationVillage ? `Village: ${p.locationVillage}` : "",
            p.capacityMt ? `Capacity: ${p.capacityMt} MT` : "",
          ]
            .filter(Boolean)
            .join("\n"),
          [
            `AI Readiness: ${p._applicationReadinessScore || 0}%`,
            `Uploaded Docs: ${p._applicationDocCount || 0}`,
          ].join("\n"),
          [
            `Workflow Status: ${p.workflowStatus || "—"}`,
            `Overall Stage: ${p.stage || "—"}`,
            `Progress: ${p.progress ?? 0}%`,
            `Last Update: ${formatDate(p.lastUpdated)}`,
          ].join("\n"),
          [
            `PMC: ${godown.pmcDecision || "—"}`,
            `Tender ID: ${godown.tenderId || "—"}`,
            `Vendor: ${godown.tenderAwardVendorName || "—"}`,
            `Amount: ${formatPDFINR(godown.tenderAwardAmount)}`,
          ].join("\n"),
          [
            `Contractor: ${
              godown.contractorName || godown.tenderAwardVendorName || "—"
            }`,
            `Mobile: ${godown.contractorMobile || "—"}`,
            `LOA: ${godown.loaGenerateDate || godown.contractorLoaDate || "—"}`,
            `Work Start: ${godown.workStartDate || "—"}`,
            `Duration: ${
              godown.workDurationDays ? `${godown.workDurationDays} Days` : "—"
            }`,
            `Construction: ${godown.constructionOverallProgress ?? 0}%`,
          ].join("\n"),
        ];
      }),
      startY: 65,
      theme: "grid",
      margin: { top: 60, left: 15, right: 15, bottom: 30 },
      styles: { fontSize: 5.8, cellPadding: 3, valign: "top" },
      headStyles: { fillColor: [6, 95, 70], textColor: [255, 255, 255] },
    });

    docPdf.save("stakeholder-premium-merged-dashboard-report.pdf");
  };

  if (loadingProfile) return <LoadingScreen />;
  if (!profile) return null;

  const typeLabel = OFFICE_TYPE_LABELS[officeType] || officeType || "—";
  const status = profile.status || "pending";
  const statusColor =
    status === "approved" ? "green" : status === "rejected" ? "red" : "yellow";

  const pageBg = dark
    ? "bg-slate-950 text-white"
    : "bg-gradient-to-br from-slate-50 via-emerald-50 to-sky-50 text-slate-900";

  return (
    <>
      <Toaster position="top-center" />

      <div className={cx("min-h-screen pb-10", pageBg)}>
        <div className="mx-auto max-w-[1500px] px-4 py-6">
          <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 shadow-[0_30px_90px_rgba(15,23,42,0.35)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.42),transparent_35%),radial-gradient(circle_at_top_right,rgba(56,189,248,0.32),transparent_32%)]" />

            <div className="relative p-6 md:p-8">
              <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-4">
                  <div className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-white/10 text-xl font-black text-white ring-1 ring-white/20 backdrop-blur">
                    {OFFICE_TYPE_SHORT[officeType] || "ST"}
                  </div>

                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.24em] text-emerald-300">
                      Stakeholder Control Room
                    </div>

                    <h1 className="mt-2 text-2xl font-black tracking-tight text-white md:text-4xl">
                      {profile.officeName || "Stakeholder Dashboard"}
                    </h1>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge color="blue">{typeLabel}</Badge>
                      <Badge color={statusColor}>
                        Status: {status.toUpperCase()}
                      </Badge>
                      <Badge color="dark">{profile.state || "Gujarat"}</Badge>
                    </div>

                    <div className="mt-3 text-sm text-slate-300">
                      {profile.fullName || "—"} · {profile.email || "—"} ·{" "}
                      {profile.phone || "—"}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setDark((d) => !d)}
                    className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold text-white backdrop-blur hover:bg-white/20"
                  >
                    {dark ? "Light Mode" : "Dark Mode"}
                  </button>

                  <button
                    type="button"
                    onClick={doLogout}
                    className="rounded-full bg-white px-4 py-2 text-xs font-black text-slate-950 shadow hover:bg-slate-100"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="-mt-6 grid gap-4 px-3 md:grid-cols-4 lg:grid-cols-8">
            <div className="lg:col-span-2">
              <MetricCard
                label="Total"
                value={totals.total}
                note="Workflow + project + direct application"
                tone="emerald"
                icon="📊"
              />
            </div>

            <div className="lg:col-span-2">
              <MetricCard
                label="Direct Applications"
                value={totals.applicationOnly}
                note="Before workflow/project creation"
                tone="blue"
                icon="📝"
              />
            </div>

            <div className="lg:col-span-2">
              <MetricCard
                label="Documents"
                value={totals.totalDocs}
                note="Downloadable uploaded records"
                tone="purple"
                icon="📁"
              />
            </div>

            <div className="lg:col-span-2">
              <MetricCard
                label="Avg AI Readiness"
                value={`${totals.avgReadiness}%`}
                note={`${totals.highReadiness} high-readiness files`}
                tone="amber"
                icon="⚡"
              />
            </div>
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-12">
            <div className="space-y-5 lg:col-span-3">
              <Panel title="Assigned Scope" subtitle="Office-wise access control">
                <div className="space-y-3">
                  <div className="rounded-3xl bg-slate-950 p-4 text-white">
                    <div className="text-[11px] font-black uppercase tracking-wider text-emerald-300">
                      Office Type
                    </div>
                    <div className="mt-1 text-lg font-black">{typeLabel}</div>
                  </div>
                </div>
              </Panel>

              <Panel title="Quick Filters" subtitle="Fast scrutiny controls">
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                      District
                    </label>
                    <select
                      value={districtFilter}
                      onChange={(e) => handleChangeDistrict(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-emerald-400"
                    >
                      {allDistrictOptions.map((d) => (
                        <option key={d} value={d}>
                          {d === "all" ? "All Districts" : d}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                      Stage
                    </label>
                    <select
                      value={stageFilter}
                      onChange={(e) => handleChangeStage(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-emerald-400"
                    >
                      {stageOptions.map((s) => (
                        <option key={s} value={s}>
                          {s === "all" ? "All Stages" : s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                      Type
                    </label>
                    <select
                      value={typeFilter}
                      onChange={(e) => handleChangeType(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-emerald-400"
                    >
                      <option value="all">All Records</option>
                      <option value="application">Direct Applications</option>
                      <option value="workflow">Workflow / Projects</option>
                      <option value="withDocs">With Documents</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={resetFilters}
                    className="w-full rounded-2xl bg-slate-900 px-4 py-2 text-sm font-black text-white shadow hover:bg-slate-800"
                  >
                    Reset Filters
                  </button>
                </div>
              </Panel>
            </div>

            <div className="space-y-5 lg:col-span-9">
              <Panel
                title="Merged Applications & Project Workflow"
                subtitle="Auto-synced from applications, workflow, tenders and contractor construction updates."
                right={
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={exportToExcel}
                      className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-xs font-black text-emerald-700 shadow-sm hover:bg-emerald-50"
                    >
                      Export Excel
                    </button>

                    <button
                      type="button"
                      onClick={exportToPDF}
                      className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-black text-white shadow hover:bg-emerald-700"
                    >
                      Export PDF
                    </button>
                  </div>
                }
              >
                <div className="mb-4">
                  <div className="relative">
                    <input
                      value={q}
                      onChange={(e) => handleChangeSearch(e.target.value)}
                      placeholder="Search by PACS, Application ID, village, mobile, registration no..."
                      className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 pr-14 text-sm font-semibold outline-none focus:border-emerald-400 focus:bg-white"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xl">
                      🔎
                    </div>
                  </div>
                </div>

                {loadingProjects ? (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500">
                    Loading live dashboard data...
                  </div>
                ) : filtered.length === 0 ? (
                  <EmptyState />
                ) : (
                  <>
                    <div className="grid gap-4">
                      {pageItems.map((p) => {
                        const godown = p._godown || {};
                        const readiness = p._applicationReadinessScore || 0;
                        const docCount = p._applicationDocCount || 0;

                        const stageColor =
                          p.stage === "Completed"
                            ? "green"
                            : p.stage === "Near Completion"
                            ? "blue"
                            : p.stage === "In Progress"
                            ? "blue"
                            : "gray";

                        return (
                          <div
                            key={p.id}
                            className={cx(
                              "rounded-[1.7rem] border bg-white p-4 shadow-[0_12px_35px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_55px_rgba(15,23,42,0.10)]",
                              selectedProjectId === p.id
                                ? "border-emerald-400 ring-4 ring-emerald-100"
                                : "border-slate-200"
                            )}
                          >
                            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge color={stageColor}>{p.stage}</Badge>

                                  {p._isApplicationOnly && (
                                    <Badge color="yellow">Application Only</Badge>
                                  )}

                                  {docCount > 0 && (
                                    <Badge color="purple">{docCount} Docs</Badge>
                                  )}

                                  {godown.applicationId && (
                                    <span className="font-mono text-[11px] font-black text-emerald-700">
                                      {godown.applicationId}
                                    </span>
                                  )}
                                </div>

                                <div className="mt-2 truncate text-lg font-black text-slate-950">
                                  {p.pacsName || "—"}
                                </div>

                                <div className="mt-1 text-sm font-semibold text-slate-500">
                                  {p.title || "—"}
                                </div>

                                <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-slate-500">
                                  <span>
                                    📍 {p.locationVillage || "—"}, {p.taluka || "—"},{" "}
                                    {p.district || "—"}
                                  </span>
                                  <span>🏗️ {p.capacityMt || "—"} MT</span>
                                  <span>📅 {formatDate(p.lastUpdated, false)}</span>
                                  {godown.contactPhone && (
                                    <span>📞 {godown.contactPhone}</span>
                                  )}
                                </div>
                              </div>

                              <div className="grid min-w-[290px] gap-3 md:grid-cols-2">
                                <div className="rounded-2xl bg-slate-50 p-3">
                                  <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-slate-500">
                                    <span>Workflow</span>
                                    <span>{p.progress || 0}%</span>
                                  </div>
                                  <div className="mt-2">
                                    <ProgressBar value={p.progress} compact />
                                  </div>
                                  <div className="mt-2 text-[11px] text-slate-500">
                                    {p.projectDashboardCurrentStage ||
                                      p.workflowStatus ||
                                      "—"}
                                  </div>
                                </div>

                                <div className="rounded-2xl bg-emerald-50 p-3">
                                  <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-emerald-700">
                                    <span>AI Readiness</span>
                                    <span>{readiness}%</span>
                                  </div>
                                  <div className="mt-2">
                                    <ProgressBar value={readiness} compact />
                                  </div>
                                  <div className="mt-2 text-[11px] text-emerald-700">
                                    Preliminary scrutiny
                                  </div>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedProjectId(p.id);
                                  setSelectedProjectData(p);
                                }}
                                className="rounded-full bg-slate-950 px-5 py-3 text-xs font-black text-white shadow hover:bg-slate-800"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-5 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-[12px] font-semibold text-slate-600 md:flex-row md:items-center md:justify-between">
                      <div>
                        Showing{" "}
                        <span className="font-black text-slate-900">
                          {filtered.length === 0 ? 0 : startIdx + 1}
                        </span>{" "}
                        -{" "}
                        <span className="font-black text-slate-900">
                          {Math.min(endIdx, filtered.length)}
                        </span>{" "}
                        of{" "}
                        <span className="font-black text-slate-900">
                          {filtered.length}
                        </span>{" "}
                        records
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={safePage === 1}
                          className={cx(
                            "rounded-full px-4 py-2 text-xs font-black",
                            safePage === 1
                              ? "bg-slate-200 text-slate-400"
                              : "bg-white text-slate-800 shadow hover:bg-slate-100"
                          )}
                        >
                          Prev
                        </button>

                        <span>
                          Page <b>{safePage}</b> / {totalPages}
                        </span>

                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={safePage === totalPages}
                          className={cx(
                            "rounded-full px-4 py-2 text-xs font-black",
                            safePage === totalPages
                              ? "bg-slate-200 text-slate-400"
                              : "bg-white text-slate-800 shadow hover:bg-slate-100"
                          )}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Panel>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex rounded-full bg-white px-5 py-2 text-sm font-bold text-emerald-700 shadow hover:bg-emerald-50"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {selectedProjectData && (
        <div className="fixed inset-0 z-[9999] bg-black/60 overflow-y-auto p-4">
          <div className="max-w-7xl mx-auto">
            <ProjectDetailView
              selectedProject={selectedProjectData}
              onClose={() => {
                setSelectedProjectId(null);
                setSelectedProjectData(null);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}